import React, { useState, useEffect, useCallback, useMemo, useRef, useDeferredValue } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Row,
  Col,
  Divider,
  Typography,
  Drawer,
  Upload,
  Statistic,
  Checkbox,
  Tag,
  Popconfirm,
  Empty,
  Pagination,
  Spin,
  Dropdown,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  DownloadOutlined,
  UploadOutlined,
  PrinterOutlined,
  TagsOutlined,
  CheckCircleFilled,
  ReloadOutlined,
} from '@ant-design/icons';
import { Grid } from 'antd';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const EAST_PRINTER_SN = '961801376';
const WEST_PRINTER_SN = '961826173';

// 物流单接口
interface LogisticsOrder {
  id?: number;
  order_no: string;
  receive_date: string;
  loading_area: string;
  shipper_id?: number;
  shipper_name: string;
  shipper_phone: string;
  unloading_station: string;
  receiver_id?: number;
  receiver_name: string;
  receiver_address: string;
  receiver_phone: string;
  goods_name: string;
  total_pieces: number;
  freight: number;
  delivery_install_fee: number;
  advance_transfer_fee: number;
  collection_amount: number;
  rebate: number;
  total_freight: number;
  cash_payment: number;
  deduction: number;
  payable_collection: number;
  settlement_method: string;
  cod_total: number;
  discount_amount: number;
  delivery_method: string;
  business_owner_id?: number;
  business_owner_name: string;
  receive_remark: string;
  status: string;
  created_at?: string;
  updated_at?: string;
}

interface SelectOption {
  label: string;
  value: number;
  displayName: string;
  isNew?: boolean;
  newName?: string;
}

// 收发货人接口
interface Consigner {
  id: number;
  name: string;
  phone: string;
  address?: string;
  subject_type: string[]; // 改为数组类型
  default_station?: string;
}

// 收发货站点接口
interface ShippingStation {
  id: number;
  name: string;
  address?: string;
  contact_person?: string;
  phone?: string;
  mobile?: string;
  status?: string;
  remark?: string;
}

// 员工接口
interface Employee {
  id: number;
  name: string;
  phone: string;
}

const LogisticsOrderManagement: React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [orders, setOrders] = useState<LogisticsOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingOrder, setEditingOrder] = useState<LogisticsOrder | null>(null);
  const [viewingOrder, setViewingOrder] = useState<LogisticsOrder | null>(null);
  const [form] = Form.useForm();
  // 隐藏到付金额复选框状态
  const [hideCodAmount, setHideCodAmount] = useState(true);
  const hideCodAmountRef = useRef(hideCodAmount);
  // 搜索状态（使用 ref 避免立即触发渲染）
  const shipperSearchValueRef = useRef('');
  const receiverSearchValueRef = useRef('');
  // 防抖后的搜索值（用于选项计算）
  const [debouncedShipperSearchValue, setDebouncedShipperSearchValue] = useState('');
  const [debouncedReceiverSearchValue, setDebouncedReceiverSearchValue] = useState('');
  useEffect(() => {
    hideCodAmountRef.current = hideCodAmount;
  }, [hideCodAmount]);
  // 防抖定时器
  const shipperSearchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const receiverSearchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  // 使用 useDeferredValue 延迟更新防抖后的值
  const deferredShipperSearchValue = useDeferredValue(debouncedShipperSearchValue);
  const deferredReceiverSearchValue = useDeferredValue(debouncedReceiverSearchValue);
  // 计算字段防抖定时器
  const calculationDebounceRef = useRef<NodeJS.Timeout | null>(null);
  // 计算结果状态（用于延迟更新）
  const [calculatedValues, setCalculatedValues] = useState<{
    total_freight: number;
    cod_total: number;
    payable_collection: number;
  }>({
    total_freight: 0,
    cod_total: 0,
    payable_collection: 0,
  });
  // 使用 useDeferredValue 延迟更新计算结果
  const deferredCalculatedValues = useDeferredValue(calculatedValues);
  // 上次计算的结果缓存（避免重复更新）
  const lastCalculationRef = useRef<{
    total_freight: number;
    cod_total: number;
    payable_collection: number;
  } | null>(null);
  // 新增/编辑弹窗 - 总件数移入清空与失焦恢复
  const modalTotalPiecesClearedRef = useRef(false);

  const handleModalTotalPiecesFocus = useCallback(() => {
    const current = form.getFieldValue('total_pieces');
    if (current === 0 && !modalTotalPiecesClearedRef.current) {
      // 使用 requestAnimationFrame 延迟到下一帧，不阻塞焦点切换
      requestAnimationFrame(() => {
        form.setFieldsValue({ total_pieces: undefined });
        modalTotalPiecesClearedRef.current = true;
      });
    }
  }, [form]);

  const handleModalTotalPiecesBlur = useCallback(() => {
    const current = form.getFieldValue('total_pieces');
    if (current === undefined || current === null || current === '') {
      form.setFieldsValue({ total_pieces: 0 });
      modalTotalPiecesClearedRef.current = false;
    } else if (current === 0) {
      // 如果值为 0，重置 cleared 标记
      modalTotalPiecesClearedRef.current = false;
    }
  }, [form]);

  // 金额字段：获取焦点时若为 0.00 则清空；失焦为空则恢复 0.00
  const modalMoneyClearedRef = useRef<Record<string, boolean>>({});
  const handleModalMoneyFocus = useCallback((field: string) => {
    const current = form.getFieldValue(field);
    if ((current === 0 || current === 0.0) && !modalMoneyClearedRef.current[field]) {
      // 使用 requestAnimationFrame 延迟到下一帧，不阻塞焦点切换
      requestAnimationFrame(() => {
        form.setFieldsValue({ [field]: undefined });
        modalMoneyClearedRef.current[field] = true;
      });
    }
  }, [form]);

  // 监听收发货人 ID，确保 options 始终包含当前值
  const receiverIdWatch = Form.useWatch('receiver_id', form);
  const shipperIdWatch = Form.useWatch('shipper_id', form);
  
  // 处理金额字段值回到 0 时重置 cleared 标记（在 onBlur 时处理，避免使用 Form.useWatch）
  // 优化：合并逻辑，减少重复调用 form.getFieldValue()
  const handleModalMoneyBlurWithReset = useCallback((field: string) => {
    const current = form.getFieldValue(field);
    // 如果值为空，设置为 0.0
    if (current === undefined || current === null || current === '') {
      form.setFieldsValue({ [field]: 0.0 });
      modalMoneyClearedRef.current[field] = false; // 设置为0时重置标记
    } else if (current === 0 || current === 0.0) {
      // 如果值已经是 0，重置 cleared 标记
      modalMoneyClearedRef.current[field] = false;
    }
  }, [form]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  // 搜索输入框 key，用于刷新时强制重新渲染
  const [searchInputKey, setSearchInputKey] = useState(0);
  // 防抖相关 ref
  const debouncedSearchRef = useRef<NodeJS.Timeout | null>(null);
  const searchValueRef = useRef(searchText); // 用于存储输入值，避免频繁更新状态
  const searchInputRef = useRef<any>(null); // 输入框 ref，用于非受控组件
  const isComposingRef = useRef(false); // 是否正在输入法组合中
  const [exporting, setExporting] = useState(false);
  // 表格排序状态（用于导出保持与界面一致的排序）
  const [tableSorter, setTableSorter] = useState<{ field?: string; order?: 'ascend' | 'descend' } | null>(null);
  
  // 新增搜索条件状态
  const [settlementMethod, setSettlementMethod] = useState<string | undefined>(undefined);
  const [startDate, setStartDate] = useState<dayjs.Dayjs>(dayjs());
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(dayjs());
  // 时间筛选复选框状态（默认选中）
  const [useTimeFilter, setUseTimeFilter] = useState<boolean>(true);

  // 批量导入相关状态
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [importData, setImportData] = useState<any[]>([]);
  const [importResult, setImportResult] = useState<any>(null);
  const [importing, setImporting] = useState(false);

  // 打印相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<LogisticsOrder[]>([]);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [deviceSelectionVisible, setDeviceSelectionVisible] = useState(false);
  const [availableDevices, setAvailableDevices] = useState<any[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<any>(null);
  const [printType, setPrintType] = useState<'receipt' | 'label'>('receipt');
  const [printing, setPrinting] = useState(false);
  const [systemSettings, setSystemSettings] = useState<any>(null);
  const [createAndPrintPrinterSn, setCreateAndPrintPrinterSn] = useState<string | null>(null);
  const createAndPrintPrinterSnRef = useRef<string | null>(null);
  // 140小单打印改为新页面方式，不再需要Modal状态

  // 下拉选项数据
  const [consigners, setConsigners] = useState<Consigner[]>([]);
  const [shippingStations, setShippingStations] = useState<ShippingStation[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  // 表格动态高度（用于虚拟滚动）
  const [tableHeight, setTableHeight] = useState(600);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  // 安全的数字格式化函数
  const formatCurrency = useCallback((value: any): string => {
    const numValue = Number(value);
    return !isNaN(numValue) ? numValue.toFixed(2) : '0.00';
  }, []);
  const isDevSkipPrintApi =
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_SKIP_PRINT_API === 'true';
  const getFinalCodAmount = useCallback((codTotal: any, shouldHide: boolean): string => {
    return String(shouldHide ? 0 : (codTotal || ''));
  }, []);

  // 结算方式选项
  const settlementMethodOptions = [
    { label: '到付款', value: '到付款' },
    { label: '现付款', value: '现付款' },
    { label: '回付款', value: '回付款' },
    { label: '月结付', value: '月结付' },
  ];

  // 送货方式选项
  const deliveryMethodOptions = [
    { label: '站点自提', value: '站点自提' },
    { label: '送货到达', value: '送货到达' },
    { label: '送货安装', value: '送货安装' },
  ];

  // 生成物流单号
  const generateOrderNo = useCallback(() => {
    const now = new Date();
    const year = String(now.getFullYear()).slice(-2);
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hour = String(now.getHours()).padStart(2, '0');
    const minute = String(now.getMinutes()).padStart(2, '0');
    const second = String(now.getSeconds()).padStart(2, '0');
    return `XY${year}${month}${day}${hour}${minute}${second}`;
  }, []);

  // 获取收发货人列表（获取全部数据）
  const fetchConsigners = useCallback(async () => {
    try {
      // 添加分页参数获取全部数据
      const params = new URLSearchParams({
        pageSize: '10000', // 设置一个足够大的数字来获取全部数据
        current: '1'
      });
      
      const response = await fetch(`/api/consigners?${params}`);
      if (response.ok) {
        const data = await response.json();
        if (data.code === 0) {
          setConsigners(data.data || []);
        }
      }
    } catch (error) {
      console.error('获取收发货人列表失败:', error);
    }
  }, []);

  // 获取收发货站点列表（获取全部数据）
  const fetchShippingStations = useCallback(async () => {
    try {
      // 添加分页参数获取全部数据
      const params = new URLSearchParams({
        pageSize: '1000', // 设置一个足够大的数字来获取全部数据
        current: '1'
      });
      
      const response = await fetch(`/api/shipping-stations?${params}`);
      if (response.ok) {
        const data = await response.json();
        if (data.code === 0) {
          console.log('收发货站点数据:', data.data);
          console.log('站点数量:', data.data.length);
          setShippingStations(data.data || []);
        }
      }
    } catch (error) {
      console.error('获取收发货站点列表失败:', error);
    }
  }, []);



  // 获取员工列表（获取全部数据）
  const fetchEmployees = useCallback(async () => {
    try {
      // 添加分页参数获取全部数据
      const params = new URLSearchParams({
        pageSize: '1000', // 设置一个足够大的数字来获取全部数据
        current: '1'
      });
      
      const response = await fetch(`/api/employees?${params}`);
      if (response.ok) {
        const data = await response.json();
        if (data.code === 0) {
          console.log('员工数据:', data.data);
          console.log('员工数量:', data.data.length);
          setEmployees(data.data || []);
        }
      }
    } catch (error) {
      console.error('获取员工列表失败:', error);
    }
  }, []);

  // 获取系统设置
  const fetchSystemSettings = useCallback(async () => {
    try {
      const response = await fetch('/api/settings');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSystemSettings(data.data);
        }
      }
    } catch (error) {
      console.error('获取系统设置失败:', error);
    }
  }, []);

  // 获取物流单列表（支持传入搜索关键词，如果不传则使用 searchText）
  const fetchOrders = useCallback(async (searchKeyword?: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        current: currentPage.toString(),
        pageSize: pageSize.toString(),
        search_keyword: searchKeyword !== undefined ? searchKeyword : searchText, // 优先使用传入的值
      });
      
      // 添加新的搜索条件
      if (settlementMethod) {
        params.append('settlement_method', settlementMethod);
      }
      // 只有当时间筛选复选框选中时才添加时间条件
      if (useTimeFilter && startDate) {
        params.append('receive_date_start', startDate.format('YYYY-MM-DD 00:00:00'));
      }
      if (useTimeFilter && endDate) {
        params.append('receive_date_end', endDate.format('YYYY-MM-DD 23:59:59'));
      }
      
      const response = await fetch(`/api/logistics-orders?${params}`);
      if (response.ok) {
        const data = await response.json();
        if (data.code === 0) {
          const newOrders = data.data || [];
          setOrders(newOrders);
          setTotal(data.total || 0);
        }
      }
    } catch (error) {
      console.error('获取物流单列表失败:', error);
      messageApi.error('获取物流单列表失败');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchText, settlementMethod, startDate, endDate, useTimeFilter, messageApi]);

  // 加载数据
  useEffect(() => {
    const loadData = async () => {
      setDataLoading(true);
      try {
        await Promise.all([
          fetchConsigners(),
          fetchShippingStations(),
          fetchEmployees(),
          fetchSystemSettings()
        ]);
      } finally {
        setDataLoading(false);
      }
    };
    loadData();
  }, [fetchConsigners, fetchShippingStations, fetchEmployees, fetchSystemSettings]);

  // 防抖处理函数：发货人搜索（使用 ref 避免立即触发渲染）
  const handleShipperSearchDebounce = useCallback((value: string) => {
    // 立即更新 ref，不触发渲染
    shipperSearchValueRef.current = value;
    
    // 清除之前的定时器
    if (shipperSearchDebounceRef.current) {
      clearTimeout(shipperSearchDebounceRef.current);
    }
    
    // 如果搜索值为空，立即更新（不需要防抖）
    if (!value) {
      setDebouncedShipperSearchValue('');
      return;
    }
    
    // 有输入时，防抖处理：300ms 延迟
    shipperSearchDebounceRef.current = setTimeout(() => {
      setDebouncedShipperSearchValue(value);
    }, 300);
  }, []);

  // 防抖处理函数：收货人搜索（使用 ref 避免立即触发渲染）
  const handleReceiverSearchDebounce = useCallback((value: string) => {
    // 立即更新 ref，不触发渲染
    receiverSearchValueRef.current = value;
    
    // 清除之前的定时器
    if (receiverSearchDebounceRef.current) {
      clearTimeout(receiverSearchDebounceRef.current);
    }
    
    // 如果搜索值为空，立即更新（不需要防抖）
    if (!value) {
      setDebouncedReceiverSearchValue('');
      return;
    }
    
    // 有输入时，防抖处理：300ms 延迟
    receiverSearchDebounceRef.current = setTimeout(() => {
      setDebouncedReceiverSearchValue(value);
    }, 300);
  }, []);

  // 清理防抖定时器（组件卸载时）
  useEffect(() => {
    return () => {
      if (shipperSearchDebounceRef.current) {
        clearTimeout(shipperSearchDebounceRef.current);
      }
      if (receiverSearchDebounceRef.current) {
        clearTimeout(receiverSearchDebounceRef.current);
      }
      if (calculationDebounceRef.current) {
        clearTimeout(calculationDebounceRef.current);
      }
    };
  }, []);

  // 监听 deferred 计算结果，更新表单字段（延迟更新，不影响输入体验）
  // 使用 ref 缓存上次更新的值，避免重复更新
  const lastFormUpdateRef = useRef<{
    total_freight: number;
    cod_total: number;
    payable_collection: number;
  } | null>(null);
  
  useEffect(() => {
    // 只在值真正变化时更新表单（避免不必要的 form.setFieldsValue 调用）
    const needsUpdate = !lastFormUpdateRef.current ||
      Math.abs((lastFormUpdateRef.current.total_freight || 0) - (deferredCalculatedValues.total_freight || 0)) > 0.01 ||
      Math.abs((lastFormUpdateRef.current.cod_total || 0) - (deferredCalculatedValues.cod_total || 0)) > 0.01 ||
      Math.abs((lastFormUpdateRef.current.payable_collection || 0) - (deferredCalculatedValues.payable_collection || 0)) > 0.01;
    
    if (needsUpdate) {
      form.setFieldsValue({
        total_freight: deferredCalculatedValues.total_freight,
        cod_total: deferredCalculatedValues.cod_total,
        payable_collection: deferredCalculatedValues.payable_collection,
      });
      
      lastFormUpdateRef.current = {
        total_freight: deferredCalculatedValues.total_freight,
        cod_total: deferredCalculatedValues.cod_total,
        payable_collection: deferredCalculatedValues.payable_collection,
      };
    }
  }, [deferredCalculatedValues, form]);


  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // 同步 searchText 到 searchValueRef（当 searchText 从外部变化时）
  useEffect(() => {
    searchValueRef.current = searchText;
  }, [searchText]);

  // 组件卸载时清理防抖定时器
  useEffect(() => {
    return () => {
      if (debouncedSearchRef.current) {
        clearTimeout(debouncedSearchRef.current);
      }
    };
  }, []);

  // 动态计算表格高度（用于虚拟滚动）
  useEffect(() => {
    const calculateTableHeight = () => {
      const windowHeight = window.innerHeight;
      // 根据移动端和桌面端计算不同的固定元素高度
      // 包括：页面 padding、Card padding、标题、搜索栏、按钮栏、分页等
      const fixedElementsHeight = isMobile 
        ? 400 // 移动端：约 400px
        : 350; // 桌面端：约 350px
      
      const calculatedHeight = windowHeight - fixedElementsHeight;
      // 设置最小高度，避免表格太小
      const minHeight = 300;
      const maxHeight = 1200; // 设置最大高度
      
      return Math.max(minHeight, Math.min(calculatedHeight, maxHeight));
    };

    // 初始计算
    setTableHeight(calculateTableHeight());

    // 防抖处理 resize 事件
    let resizeTimer: NodeJS.Timeout | null = null;
    const handleResize = () => {
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = setTimeout(() => {
        setTableHeight(calculateTableHeight());
      }, 150); // 150ms 防抖
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
    };
  }, [isMobile]); // 依赖 isMobile，当屏幕尺寸变化时重新计算

  // 员工列表加载后，若存在“刘金玉”且当前未选择业务归属，则默认选中
  useEffect(() => {
    if (!modalVisible) return; // 仅在弹窗打开时尝试默认选择
    if (editingOrder) return; // 编辑时不覆盖已有值
    if (!employees || employees.length === 0) return;
    const target = employees.find(e => e.name === '刘金玉');
    if (!target) return;
    const current = form.getFieldValue('business_owner_id');
    if (current === undefined || current === null || current === '') {
      form.setFieldsValue({ business_owner_id: target.id });
    }
  }, [employees, form, modalVisible, editingOrder]);


  // 初始化时设置默认日期为当天
  useEffect(() => {
    const today = dayjs();
    setStartDate(today);
    setEndDate(today);
  }, []);

  // 处理搜索
  // 处理搜索输入（带防抖）
  const handleSearch = useCallback((value: string) => {
    // 立即更新 ref，不触发渲染
    searchValueRef.current = value;
    
    // 如果正在输入法组合中，不判断是否为汉字，等待组合完成
    if (!isComposingRef.current) {
      // 检测输入内容，自动设置时间筛选复选框状态
      if (value.trim()) {
        // 判断是否为纯字母或纯数字
        const isPureAlphanumeric = /^[a-zA-Z0-9]+$/.test(value.trim());
        // 判断是否包含汉字
        const hasChinese = /[\u4e00-\u9fa5]/.test(value);
        
        if (isPureAlphanumeric) {
          // 纯字母或纯数字，设为未选中
          setUseTimeFilter(false);
        } else if (hasChinese) {
          // 包含汉字，设为选中
          setUseTimeFilter(true);
        }
      } else {
        // 输入为空，恢复默认选中
        setUseTimeFilter(true);
      }
    }
    
    // 清除之前的定时器
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current);
    }
    
    // 延迟更新状态和执行搜索（500ms 防抖）
    debouncedSearchRef.current = setTimeout(() => {
      const finalValue = searchValueRef.current;
      setSearchText(finalValue);
      setCurrentPage(1);
      
      // 直接调用 fetchOrders，传入搜索关键词
      fetchOrders(finalValue);
    }, 500);
  }, [fetchOrders]);

  // 处理输入法组合开始
  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  // 处理输入法组合结束
  const handleCompositionEnd = useCallback((e: React.CompositionEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    searchValueRef.current = value;
    
    // 使用 requestAnimationFrame 确保在下一个渲染周期再判断
    // 这样可以确保所有 onChange 事件都处理完，避免在组合过程中提前判断
    requestAnimationFrame(() => {
      // 再次延迟，确保 onChange 事件完全处理完
      setTimeout(() => {
        isComposingRef.current = false;
        
        // 获取最新的值（可能在 onCompositionEnd 之后又有 onChange）
        const finalValue = searchInputRef.current?.input?.value || searchValueRef.current;
        
        // 输入法组合完成后，判断是否为汉字
        if (finalValue.trim()) {
          // 判断是否为纯字母或纯数字
          const isPureAlphanumeric = /^[a-zA-Z0-9]+$/.test(finalValue.trim());
          // 判断是否包含汉字
          const hasChinese = /[\u4e00-\u9fa5]/.test(finalValue);
          
          if (isPureAlphanumeric) {
            // 纯字母或纯数字，设为未选中
            setUseTimeFilter(false);
          } else if (hasChinese) {
            // 包含汉字，设为选中
            setUseTimeFilter(true);
          }
        } else {
          // 输入为空，恢复默认选中
          setUseTimeFilter(true);
        }
      }, 10);
    });
  }, []);

  // 刷新并重置所有状态
  const handleRefresh = useCallback(() => {
    // 清除选中状态
    setSelectedRowKeys([]);
    setSelectedRows([]);
    // 清空搜索框
    setSearchText('');
    searchValueRef.current = '';
    // 更新搜索输入框 key，强制重新渲染以清空输入框
    setSearchInputKey(prev => prev + 1);
    // 恢复时间筛选为默认选中
    setUseTimeFilter(true);
    // 清除防抖定时器
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current);
      debouncedSearchRef.current = null;
    }
    // 清空收发货人搜索值（使用 ref 和防抖函数）
    shipperSearchValueRef.current = '';
    receiverSearchValueRef.current = '';
    handleShipperSearchDebounce('');
    handleReceiverSearchDebounce('');
    // 重置时间控件为当天
    const today = dayjs();
    setStartDate(today);
    setEndDate(today);
    // 重置结算方式
    setSettlementMethod(undefined);
    // 重置页码
    setCurrentPage(1);
    // 重置"隐藏到付金额"复选框为默认勾选状态
    setHideCodAmount(true);
    // 触发数据刷新
    fetchOrders('');
  }, [handleShipperSearchDebounce, handleReceiverSearchDebounce, fetchOrders]);

  // 清空搜索
  const handleClearSearch = useCallback(() => {
    setSearchText('');
    setCurrentPage(1);
  }, []);

  // 处理高级搜索条件变化
  const handleAdvancedSearchChange = useCallback(() => {
    setCurrentPage(1);
    // 触发搜索
    fetchOrders(searchText);
  }, [searchText, fetchOrders]);





  // 清理 Modal 相关状态（统一清理函数）
  const cleanupModalState = useCallback(() => {
    // 清空搜索值（避免下次打开时残留）
    shipperSearchValueRef.current = '';
    receiverSearchValueRef.current = '';
    handleShipperSearchDebounce('');
    handleReceiverSearchDebounce('');
    // 清理计算防抖定时器
    if (calculationDebounceRef.current) {
      clearTimeout(calculationDebounceRef.current);
      calculationDebounceRef.current = null;
    }
    // 重置计算结果缓存
    lastCalculationRef.current = null;
    // 重置表单值缓存
    formValuesCacheRef.current = {};
    // 重置表单更新缓存
    lastFormUpdateRef.current = null;
    // 重置计算结果状态
    setCalculatedValues({
      total_freight: 0,
      cod_total: 0,
      payable_collection: 0,
    });
  }, [handleShipperSearchDebounce, handleReceiverSearchDebounce]);

  // 显示新增/编辑模态框
  const showModal = useCallback((order?: LogisticsOrder) => {
    if (order) {
      setEditingOrder(order);
      form.setFieldsValue({
        ...order,
        receive_date: order.receive_date ? dayjs(order.receive_date) : undefined,
      });
    } else {
      setEditingOrder(null);
      form.resetFields();
      // 设置默认值
      const defaultLoadingArea = shippingStations.find(s => s.name === '香河')?.name || '';
      form.setFieldsValue({
        order_no: generateOrderNo(),
        receive_date: dayjs(),
        loading_area: defaultLoadingArea,
        settlement_method: '到付款',
        delivery_method: '站点自提',
      });
    }
    // 清空搜索值（避免下次打开时残留）
    cleanupModalState();
    
    // 先打开 Modal，延迟初始化计算结果（避免阻塞 Modal 打开）
    setModalVisible(true);
    
    // 延迟初始化计算结果（在 Modal 打开后异步计算，不阻塞 UI）
    setTimeout(() => {
      const currentValues = form.getFieldsValue();
      const totalFreight = calculateTotalFreight(currentValues);
      const codTotal = calculateCodTotal(totalFreight, currentValues);
      const payableCollection = calculatePayableCollection(currentValues.collection_amount);
      
      const safeTotalFreight = isNaN(totalFreight) || !isFinite(totalFreight) ? 0 : totalFreight;
      const safeCodTotal = isNaN(codTotal) || !isFinite(codTotal) ? 0 : codTotal;
      const safePayableCollection = isNaN(payableCollection) || !isFinite(payableCollection) ? 0 : payableCollection;
      
      setCalculatedValues({
        total_freight: safeTotalFreight,
        cod_total: safeCodTotal,
        payable_collection: safePayableCollection,
      });
      
      // 同步缓存
      formValuesCacheRef.current = { ...currentValues };
    }, 0);
  }, [form, generateOrderNo, shippingStations, consigners, cleanupModalState]);

  // 显示详情抽屉
  const showDrawer = useCallback((order: LogisticsOrder) => {
    setViewingOrder(order);
    setDrawerVisible(true);
  }, []);

  // 创建收发货人
  const createConsigner = useCallback(async (consignerData: any) => {
    try {
      const response = await fetch('/api/consigners', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(consignerData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.code === 0) {
          return result.data.id;
        } else {
          throw new Error(result.message || '创建收发货人失败');
        }
      } else {
        throw new Error('创建收发货人失败');
      }
    } catch (error) {
      console.error('创建收发货人失败:', error);
      throw error;
    }
  }, []);

  // 处理表单提交
  const handleSubmit = useCallback(async (values: any) => {
    try {
      const targetPrinterSn = createAndPrintPrinterSnRef.current || createAndPrintPrinterSn;
      setCreateAndPrintPrinterSn(null); // 重置状态
      createAndPrintPrinterSnRef.current = null;
      
      console.log('🔍 表单提交时的values:', values);
      
      let shipperId = values.shipper_id;
      let receiverId = values.receiver_id;
      
      // 处理发货人
      let newShipperName = values.shipper_name;
      if (values.shipper_id === -1) {
        console.log('🔍 准备创建发货人，shipper_name:', values.shipper_name);
        // 新增发货人
        const shipperData = {
          name: values.shipper_name,
          phone: values.shipper_phone || '',
          subject_type: '发货人',
          default_station: values.loading_area || '', // 使用装货区，不是卸货站点
          status: '开启'
        };
        console.log('🔍 发货人数据:', shipperData);
        shipperId = await createConsigner(shipperData);
        console.log('创建发货人成功，ID:', shipperId);
        // 新创建的收发货人名称直接使用输入值
        newShipperName = values.shipper_name;
        // 注意：不在这里刷新列表，因为创建物流单时不需要从列表中查找
        // 列表刷新会在创建物流单成功后统一进行
      }
      
      // 处理收货人
      let newReceiverName = values.receiver_name;
      if (values.receiver_id === -1) {
        console.log('🔍 准备创建收货人，receiver_name:', values.receiver_name);
        // 新增收货人
        const receiverData = {
          name: values.receiver_name,
          phone: values.receiver_phone || '',
          address: values.receiver_address || '',
          subject_type: '收货人',
          default_station: values.unloading_station || '',
          status: '开启'
        };
        console.log('🔍 收货人数据:', receiverData);
        receiverId = await createConsigner(receiverData);
        console.log('创建收货人成功，ID:', receiverId);
        // 新创建的收发货人名称直接使用输入值
        newReceiverName = values.receiver_name;
        // 注意：不在这里刷新列表，因为创建物流单时不需要从列表中查找
        // 列表刷新会在创建物流单成功后统一进行
      }
      
      // 根据ID获取发货人和收货人的姓名
      // 如果是新创建的，直接使用 newShipperName/newReceiverName
      // 如果是已存在的，从列表中查找（如果找不到，使用传入的名称）
      const shipper = consigners.find(c => c.id === shipperId);
      const receiver = consigners.find(c => c.id === receiverId);
      const businessOwner = employees.find(e => e.id === values.business_owner_id);
      
      const orderData = {
        ...values,
        shipper_id: shipperId,
        receiver_id: receiverId,
        receive_date: values.receive_date?.format('YYYY-MM-DD HH:mm:ss'),
        // 添加姓名字段：优先使用新创建的名称，其次从列表中找到的，最后使用传入的值
        shipper_name: newShipperName || shipper?.name || values.shipper_name || '',
        receiver_name: newReceiverName || receiver?.name || values.receiver_name || '',
        business_owner_name: businessOwner?.name || '',
      };

      if (editingOrder?.id) {
        // 更新
        const response = await fetch(`/api/logistics-orders/${editingOrder.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(orderData),
        });
        
        if (response.ok) {
          messageApi.success('更新成功');
          cleanupModalState();
          setModalVisible(false);
          fetchOrders();
        } else {
          messageApi.error('更新失败');
        }
      } else {
        // 创建
        const response = await fetch('/api/logistics-orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(orderData),
        });
        
        if (response.ok) {
          const result = await response.json();
          messageApi.success('创建成功');
          
          // 如果需要打印，调用打印功能
          if (targetPrinterSn) {
            try {
              // 使用完整的订单数据，包含创建后的ID
              const orderDataWithId = {
                ...orderData,
                id: result.data?.id || result.id,
              };
              await handlePrintAfterCreate(orderDataWithId, targetPrinterSn);
            } catch (printError) {
              console.error('打印失败:', printError);
              messageApi.warning('创建成功，但打印失败');
            }
          }
          
          cleanupModalState();
          setModalVisible(false);
          // 刷新列表（包括收发货人列表，以便下次打开时能找到新创建的）
          // 使用 catch 避免刷新失败影响用户体验
          fetchConsigners().catch(err => console.error('刷新收发货人列表失败:', err));
          fetchOrders();
        } else {
          messageApi.error('创建失败');
        }
      }
    } catch (error) {
      console.error('提交失败:', error);
      messageApi.error('提交失败');
    }
  }, [editingOrder, fetchOrders, consigners, employees, createAndPrintPrinterSn, createConsigner, fetchConsigners]);

  // 创建后打印功能
  const handlePrintAfterCreate = useCallback(async (orderData: any, targetPrinterSn: string) => {
    console.log('开始创建后打印流程');
    console.log('订单数据:', orderData);
    console.log('目标打印机编号:', targetPrinterSn);
    
    try {
      let processedOrderData = { ...orderData };
      
      // 处理发货人
      if (orderData.shipper_id === -1) {
        // 新增发货人
        const shipperData = {
          name: orderData.shipper_name,
          phone: orderData.shipper_phone || '',
          subject_type: '发货人',
          default_station: orderData.unloading_station || '',
          status: '开启'
        };
        const shipperId = await createConsigner(shipperData);
        processedOrderData.shipper_id = shipperId;
        console.log('创建发货人成功，ID:', shipperId);
      }
      
      // 处理收货人
      if (orderData.receiver_id === -1) {
        // 新增收货人
        const receiverData = {
          name: orderData.receiver_name,
          phone: orderData.receiver_phone || '',
          address: orderData.receiver_address || '',
          subject_type: '收货人',
          default_station: orderData.unloading_station || '',
          status: '开启'
        };
        const receiverId = await createConsigner(receiverData);
        processedOrderData.receiver_id = receiverId;
        console.log('创建收货人成功，ID:', receiverId);
      }
      
      // 获取云打印机设备
      const response = await fetch('/api/devices?device_category=云打印机');
      
      if (response.ok) {
        const data = await response.json();
        console.log('设备列表响应:', data);
        
        if (data.success && data.data.length > 0) {
          const availableDevices = data.data.filter((device: any) => device.status === '已绑定');
          
          if (availableDevices.length > 0) {
            const device = availableDevices.find((item: any) => item.device_sn === targetPrinterSn);
            if (!device) {
              console.log('❌ 没有找到指定编号的已绑定打印设备:', targetPrinterSn);
              messageApi.warning(`未找到编号 ${targetPrinterSn} 的已绑定云打印机`);
              return;
            }
            console.log('使用设备:', device);
            
            // 获取系统设置
            const settingsResponse = await fetch('/api/settings');
            const settingsData = await settingsResponse.json();
            const systemSettings = settingsData.success ? settingsData.data : null;
            
            // 使用与云打印相同的前端替换逻辑
            const template = device.template || `<CB><B>{0}</B></CB><BR>
物流单号:{1}<BR>
日期:{2}<BR>
---------------------------------<BR>
收货站点：<B>{3}</B><BR>
收货人：<B>{4}</B><BR>
收货电话：<B>{5}</B><BR>
地址：<B>{6}</B><BR>
---------------------------------<BR>
托运人：{7}<BR>
厂家电话：{8}<BR>
---------------------------------<BR>
货品名称：{9}<BR>
送货方式：{10}<BR>
件数：<B>{11}</B><BR>
按计：<BR>
代收货款：{12}<BR>
现付款：{13}<BR>
垫付款：{14}<BR>
到付金额：{15}<BR>
备注：{16}<BR>
{17}`;
            
            // 前端替换占位符
            let printContent = template;
            const replacements: Record<string, string> = {
              '{0}': systemSettings?.company_short_name || '兴运物流',
              '{1}': orderData.order_no || '',
              '{2}': orderData.receive_date || '',
              '{3}': orderData.unloading_station || '',
              '{4}': orderData.receiver_name || '',
              '{5}': orderData.receiver_phone || '',
              '{6}': orderData.receiver_address || '',
              '{7}': orderData.shipper_name || '',
              '{8}': orderData.shipper_phone || '',
              '{9}': orderData.goods_name || '',
              '{10}': orderData.delivery_method || '',
              '{11}': String(orderData.total_pieces || ''),
              '{12}': formatCurrency(orderData.collection_amount),
              '{13}': formatCurrency(orderData.cash_payment),
              '{14}': formatCurrency(orderData.advance_transfer_fee),
              '{15}': formatCurrency(hideCodAmount ? 0 : orderData.cod_total),
              '{16}': String(orderData.receive_remark || ''),
              '{17}': systemSettings?.company_remark || ''
            };

            // 执行替换
            Object.keys(replacements).forEach(key => {
              printContent = printContent.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replacements[key]);
            });

            // 调用打印API
            const printResponse = await fetch('/api/devices/print-order', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                orderData: orderData,
                deviceSn: device.sn || device.device_sn || device.deviceSn,
                template: printContent,
                times: 1
              }),
            });
            
            if (printResponse.ok) {
              const printResult = await printResponse.json();
              console.log('✅ 打印成功:', printResult);
              messageApi.success('打印成功！');
            } else {
              console.error('❌ 打印失败:', printResponse.status);
              messageApi.error('打印失败');
            }
          } else {
            console.log('❌ 没有可用的打印设备');
            messageApi.warning('没有可用的打印设备');
          }
        } else {
          console.log('❌ 没有找到打印设备');
          messageApi.warning('没有找到打印设备');
        }
      } else {
        console.error('❌ 获取设备列表失败:', response.status);
        messageApi.error('获取设备列表失败');
      }
    } catch (error) {
      console.error('❌ 打印过程中出错:', error);
      messageApi.error('打印过程中出错');
    }
  }, [messageApi, hideCodAmount, formatCurrency, createConsigner]);

  // 删除物流单
  const handleDelete = useCallback(async (id: number) => {
    try {
      const response = await fetch(`/api/logistics-orders/${id}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        messageApi.success('删除成功');
        fetchOrders();
      } else {
        messageApi.error('删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      messageApi.error('删除失败');
    }
  }, [fetchOrders]);

  // 处理发货人选择
  const handleShipperChange = useCallback((shipperId: number) => {
    const shipper = consigners.find(c => c.id === shipperId);
    if (shipper) {
      form.setFieldsValue({
        shipper_name: shipper.name,        // 显示名称
        shipper_phone: shipper.phone,      // 自动填充电话
        loading_area: shipper.default_station || form.getFieldValue('loading_area'),
      });
    }
  }, [consigners, form]);

  // 处理收货人选择
  const handleReceiverChange = useCallback((receiverId: number) => {
    const receiver = consigners.find(c => c.id === receiverId);
    if (receiver) {
      form.setFieldsValue({
        receiver_name: receiver.name,        // 显示名称
        receiver_phone: receiver.phone,      // 自动填充电话
        receiver_address: receiver.address || '', // 自动填充地址
        unloading_station: receiver.default_station || '', // 自动填充卸货站点
      });
    }
  }, [consigners, form]);

  // 处理装货区选择
  const handleLoadingAreaChange = useCallback((value: string) => {
    form.setFieldsValue({ loading_area: value });
  }, [form]);

  // 处理卸货站点选择
  const handleUnloadingStationChange = useCallback((value: string) => {
    form.setFieldsValue({ unloading_station: value });
  }, [form]);

  // 处理业务归属选择
  const handleBusinessOwnerChange = useCallback((employeeId: number) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      form.setFieldsValue({ business_owner_name: employee.name });
    }
  }, [employees, form]);

  // 自动计算总运费
  const calculateTotalFreight = useCallback((values: any) => {
    const freight = Number(values.freight || 0);
    const deliveryInstallFee = Number(values.delivery_install_fee || 0);
    const advanceTransferFee = Number(values.advance_transfer_fee || 0);
    const collectionAmount = Number(values.collection_amount || 0);
    const rebate = Number(values.rebate || 0);
    
    return freight + deliveryInstallFee + advanceTransferFee + collectionAmount + rebate;
  }, []);

  // 自动计算到付金额
  const calculateCodTotal = useCallback((totalFreight: number, values: any) => {
    const cashPayment = Number(values.cash_payment || 0);
    const deduction = Number(values.deduction || 0);
    return totalFreight - cashPayment - deduction;
  }, []);

  // 自动计算应付代收款
  const calculatePayableCollection = useCallback((collectionAmount: number) => {
    return -(Number(collectionAmount || 0));
  }, []);

  // 打印相关函数
  // 处理多选行选择
  const handleRowSelectionChange = useCallback((selectedRowKeys: React.Key[], selectedRows: LogisticsOrder[]) => {
    console.log('handleRowSelectionChange 被调用');
    console.log('选中的行键:', selectedRowKeys);
    console.log('选中的行数据:', selectedRows);
    setSelectedRowKeys(selectedRowKeys);
    setSelectedRows(selectedRows);
    console.log('多选状态已更新');
  }, []);

  // 处理打印按钮点击
  const handlePrintClick = useCallback(async (type: 'receipt' | 'label', targetPrinterSn?: string) => {
    console.log('handlePrintClick 被调用，类型:', type);
    console.log('目标打印机编号:', targetPrinterSn);
    console.log('当前选中的多选订单:', selectedRows);
    
    if (selectedRows.length === 0) {
      console.log('❌ 没有选中订单，显示警告');
      messageApi.warning('请先选择一个或多个物流单');
      return;
    }
    
    console.log('订单验证通过，开始设备筛选');
    setPrintType(type);
    setSelectedDevice(null);
    
    // 根据打印类型筛选设备
    const deviceCategory = type === 'receipt' ? '云打印机' : '云标签机';
    console.log('要查找的设备类型:', deviceCategory);
    
    try {
      console.log('开始请求设备列表...');
      // 调用后端API获取设备列表
      const response = await fetch(`/api/devices?device_category=${encodeURIComponent(deviceCategory)}`);
      console.log('设备列表请求响应状态:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.length > 0) {
          const filteredDevices = data.data.filter((device: any) => device.status === '已绑定');
          
          setAvailableDevices(filteredDevices);
          
          if (filteredDevices.length === 0) {
            console.log('❌ 没有找到可用的设备，显示警告');
            messageApi.warning(`没有找到可用的${deviceCategory}`);
            return;
          }
          
          if (targetPrinterSn) {
            const targetDevice = filteredDevices.find((device: any) => device.device_sn === targetPrinterSn);

            if (!targetDevice) {
              console.log('❌ 没有找到指定编号的已绑定设备:', targetPrinterSn);
              messageApi.warning(`未找到编号 ${targetPrinterSn} 的已绑定${deviceCategory}`);
              return;
            }

            console.log('找到目标设备，直接调用打印:', targetDevice);
            setSelectedDevice(targetDevice);
            handleBatchPrintWithDevice(targetDevice, selectedRows, 'receipt');
          } else if (filteredDevices.length === 1) {
            console.log('找到1个设备，自动选择并调用打印');
            console.log('选中的设备:', filteredDevices[0]);
            setSelectedDevice(filteredDevices[0]);
            console.log('即将调用 handlePrint...');
            // 直接传递设备参数，避免状态更新异步问题
            handleBatchPrintWithDevice(filteredDevices[0], selectedRows, deviceCategory === '云打印机' ? 'receipt' : 'label');
          } else {
            console.log('找到多个设备，显示选择弹窗');
            setDeviceSelectionVisible(true);
          }
        } else {
          console.log('❌ 设备列表为空或请求失败');
          messageApi.warning(`没有找到可用的${deviceCategory}`);
        }
      } else {
        console.log('❌ 设备列表请求失败，状态码:', response.status);
        messageApi.error('获取设备列表失败');
      }
    } catch (error) {
      console.error('❌ 获取设备列表异常:', error);
              messageApi.error('获取设备列表失败');
    }
  }, [selectedRows, messageApi]);

  // 处理140小单打印点击 - 改为新页面方式
  const handlePrint140Click = useCallback(() => {
    console.log('handlePrint140Click 被调用');
    console.log('当前选中的多选订单:', selectedRows);
    console.log('🔘 当前隐藏到付金额状态:', hideCodAmount);
    
    if (selectedRows.length === 0) {
      console.log('❌ 没有选中订单，显示警告');
      messageApi.warning('请先选择一个或多个物流单');
      return;
    }
    
    // 获取选中的物流单数据
    const selectedOrdersData = selectedRows.map(selectedOrder => {
      const latestOrder = orders.find(order => order.id === selectedOrder.id) || selectedOrder;
      return latestOrder;
    });
    
    // 创建新页面内容，传递 hideCodAmount 状态
    const printContent = generate140PrintContent(selectedOrdersData, hideCodAmount);
    
    // 打开新页面（新标签页）
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
    }
  }, [selectedRows, messageApi, orders, hideCodAmount]);

  // 生成140小单打印内容 - 按照demo样式实现
  const generate140PrintContent = (ordersData: LogisticsOrder[], hideCodAmountValue: boolean) => {
    const companyName = systemSettings?.company_short_name || '兴运物流';
    
    return `
<!DOCTYPE html>
<html lang="zh-CN">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>140小单打印-智子物流系统</title>
    <style>
        body {
            font-family: '黑体', 'Microsoft YaHei', sans-serif;
            margin: 0;
            padding: 20px;
            background: white;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
        }
        
        .print-container {
            max-width: 500px;
            width: 100%;
        }
        
        .print-btn-box {
            text-align: center;
            margin-bottom: 20px;
        }
        
        .print-btn {
            background: #1890ff;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 16px;
        }
        
        .print-table {
            width: 100%;
        }
        
        .tableMain {
            width: 100%;
            border: 0;
            cellpadding: 0;
            cellspacing: 5;
            text-align: left;
            font-family: '黑体', 'Microsoft YaHei', sans-serif;
            font-size: 18px;
        }
        
        .tableMain th {
            font-weight: bold;
            padding: 2px 4px;
            line-height: 1.2;
        }
        
        .tableMain td {
            padding: 2px 4px;
            line-height: 1.2;
        }
        
        .title {
            font-size: 26px;
            text-align: center;
            line-height: 1.2;
        }
        
        .strong-text {
            font-size: 22px;
            font-weight: bold;
        }
        
        .terms {
            font-family: '宋体', SimSun, serif;
            font-size: 16px;
            line-height: 1.3;
        }
        
        /* 分页样式 - 按照demo实现 */
        .page-break {
            page-break-after: always;
            margin-bottom: 20px;
        }
        
        .page-break:last-child {
            page-break-after: auto;
        }
        
        @media print {
            body {
                margin: 0;
                padding: 0;
            }
            
            .print-container {
                max-width: 100%;
            }
            
            .print-btn-box {
                display: none;
            }
        }
    </style>
</head>
<body>
    <section id="printMain" style="max-width:500px;">
        <div class="print-btn-box">
            <button type="button" class="print-btn" onclick="window.print()" title="立即打印">
                🖨️ 立即打印
            </button>
        </div>
        
        <div class="print-table" id="print-table">
            ${ordersData.map((order, index) => `
                <table width="100%" border="0" cellpadding="0" cellspacing="5" class="tableMain" style="text-align:left;font-family:'黑体';font-size:18px;">
                    <tbody>
                        <tr>
                            <th colspan="2" style="font-size:26px; text-align:center; line-height:36px;">${companyName}托运单</th>
                        </tr>
                        <tr>
                            <th>物流单号：${order.order_no || ''}</th>
                            <td rowspan="4" style="text-align: center;">
                                <div id="qrcode-${order.id || index}" style="width: 100px; height: 100px; display: flex; align-items: center; justify-content: center;">
                                    <!-- 二维码将通过JavaScript生成 -->
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>日期：${order.receive_date ? new Date(order.receive_date).toLocaleString('sv-SE', {
                                timeZone: 'Asia/Shanghai',
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: false
                            }) : ''}</td>
                        </tr>
                        <tr>
                            <td>收货站点：<strong class="strong-text">${order.unloading_station || ''}</strong></td>
                        </tr>
                        <tr>
                            <td>收货人：${order.receiver_name || ''}</td>
                        </tr>
                        <tr>
                            <th colspan="2">收货电话：${order.receiver_phone || ''}</th>
                        </tr>
                        <tr>
                            <td colspan="2">收货地址：${order.receiver_address || ''}</td>
                        </tr>
                        <tr>
                            <th>货物名称：${order.goods_name || ''}</th>
                            <th>代收款：${order.collection_amount ? `¥${order.collection_amount}` : ''}</th>
                        </tr>
                        <tr>
                            <th>件数：${order.total_pieces || ''}</th>
                            <th>垫付款：${order.advance_transfer_fee ? `¥${order.advance_transfer_fee}` : ''}</th>
                        </tr>
                        <tr>
                            <th>送货方式：${order.delivery_method || ''}</th>
                            <th>现付款：${order.cash_payment ? `¥${order.cash_payment}` : ''}</th>
                        </tr>
                        <tr>
                            <th>托运人：${order.shipper_name || ''}</th>
                            <th>回付款：¥0</th>
                        </tr>
                        <tr>
                            <th>发货电话：${order.shipper_phone || ''}</th>
                            <th>到付金额：${hideCodAmountValue ? '¥0' : (order.cod_total ? `¥${order.cod_total}` : '')}</th>
                        </tr>
                        <tr>
                            <td colspan="2">备注：${order.receive_remark || ''}</td>
                        </tr>
                        <tr>
                            <td>注意事项：</td>
                            <td>签字：</td>
                        </tr>
                        <tr>
                            <td colspan="2" class="terms">
                                1.托运货物必须按实际价值保险，否则造成意外损失由发货人自负。未保价的货物如有丢失，一律按照货物运费的三倍赔偿，最高不超过500元。2.隐瞒托运危险品和国家禁运物品，出现问题，托运人负全部责任。无包装或包装不合格的产品，运输途中的损坏，货站不负责赔偿。玻璃、陶瓷、理石、岩板等易碎易损物品运输过程中的损坏，货站不负责赔偿。香河：18632689345 保定13303023075 容城：19133221600 清苑：13398631187
                            </td>
                        </tr>
                    </tbody>
                </table>
                ${index < ordersData.length - 1 ? '<div style="page-break-after:always; margin-bottom: 20px"></div>' : ''}
            `).join('')}
        </div>
    </section>
    
    <script src="https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js"></script>
    <script>
        // 生成无边框二维码
        function generateQRCode(elementId, text) {
            try {
                const element = document.getElementById(elementId);
                if (element) {
                    console.log("开始生成二维码:", text, "长度:", text.length);
                    
                    // 创建二维码实例 (版本4, 容错级别L)
                    var qr = qrcode(4, 'L');
                    qr.addData(text); // 添加内容
                    qr.make(); // 生成二维码
                    
                    // 创建无空白边距的二维码
                    // createImgTag的第三个参数控制边距，0表示无空白
                    // 第一个参数控制像素大小，改为3让二维码更小
                    element.innerHTML = qr.createImgTag(3, 0);
                    
                    console.log("二维码生成成功:", text);
                } else {
                    console.error("找不到元素:", elementId);
                }
            } catch (error) {
                console.error("二维码生成失败:", error, "内容:", text);
                const element = document.getElementById(elementId);
                if (element) {
                    element.innerHTML = '<div style="font-size: 10px; color: #666; text-align: center; padding: 10px; border: 1px solid #ccc;">二维码生成失败: ' + error.message + '</div>';
                }
            }
        }
        
        // 页面加载完成后生成二维码并自动打印
        window.onload = function() {
            // 等待qrcode库加载完成
            const checkQRCode = setInterval(() => {
                if (typeof qrcode !== 'undefined') {
                    clearInterval(checkQRCode);
                    
                    // 生成所有二维码
                    ${ordersData.map((order, index) => `
                        generateQRCode('qrcode-${order.id || index}', '${order.order_no || ''} | www.geeker.net.cn | 18600034103,15811322824');
                    `).join('')}
                    
                    // 延迟打印，确保二维码生成完成
                    setTimeout(function() {
                        window.print();
                    }, 1500);
                }
            }, 100);
            
            // 10秒后如果还没加载完成，直接打印
            setTimeout(() => {
                if (typeof qrcode === 'undefined') {
                    console.warn('qrcode库加载超时，直接打印');
                    window.print();
                }
            }, 10000);
        };
    </script>
</body>
</html>
    `;
  };

  // 批量打印函数
  const handleBatchPrintWithDevice = useCallback(async (device: any, ordersToPrint: LogisticsOrder[], printType: 'receipt' | 'label') => {
    console.log('handleBatchPrintWithDevice 被调用');
    console.log('要打印的订单数量:', ordersToPrint.length);
    console.log('使用的设备:', device);
    console.log('打印类型:', printType);
    console.log('🔘 当前复选框状态:', hideCodAmountRef.current);
    
    if (!device || ordersToPrint.length === 0) {
      console.log('❌ 缺少必要参数');
      messageApi.error('缺少必要参数');
      return;
    }

    setPrinting(true);
    console.log('开始批量打印流程');

    try {
      let template: string;
      let apiEndpoint: string;

      if (printType === 'receipt') {
        console.log('📄 使用云打印机模板');
        template = device.template || `<CB><B>{0}</B></CB><BR>
物流单号:{1}<BR>
日期:{2}<BR>
--------------------------------<BR>
收货站点：<B>{3}</B><BR>
收货人：<B>{4}</B><BR>
收货电话：<B>{5}</B><BR>
地址：<B>{6}</B><BR>
--------------------------------<BR>
托运人：{7}<BR>
厂家电话：{8}<BR>
--------------------------------<BR>
货品名称：{9}<BR>
送货方式：{10}<BR>
件数：<B>{11}</B><BR>
按计：<BR>
代收货款：{12}<BR>
现付款：{13}<BR>
垫付款：{14}<BR>
到付金额：{15}<BR>
备注：{16}<BR>
{17}`;
        apiEndpoint = '/api/devices/print-order';
      } else {
        console.log('使用云标签机模板');
        template = device.template || `<TEXT x="10" y="10" font="12" w="3" h="3" r="0">{0}</TEXT>
<TEXT x="10" y="100" font="12" w="2" h="2" r="0">{1}</TEXT>
<TEXT x="200" y="155" font="12" w="1" h="1" r="0">{4}/{2}件</TEXT>
<TEXT x="10" y="200" font="12" w="1" h="1" r="0">{3}</TEXT>`;
        apiEndpoint = '/api/devices/print-label';
      }

      console.log('原始模板:', template);
      
      // 循环打印每个订单
      for (let i = 0; i < ordersToPrint.length; i++) {
        const order = ordersToPrint[i];
        console.log(`开始打印第 ${i + 1}/${ordersToPrint.length} 个订单:`, order.order_no);
        
        if (printType === 'receipt') {
          // 云打印机占位符替换
          let printContent = template;
          
          const replacements: Record<string, string> = {
            '{0}': systemSettings?.company_short_name || '物流公司',
            '{1}': order.order_no || '',
            '{2}': order.receive_date ? dayjs(order.receive_date).format('YYYY-MM-DD HH:mm:ss') : '',
            '{3}': order.unloading_station || '',
            '{4}': order.receiver_name || '',
            '{5}': order.receiver_phone || '',
            '{6}': order.receiver_address || '',
            '{7}': order.shipper_name || '',
            '{8}': order.shipper_phone || '',
            '{9}': order.goods_name || '',
            '{10}': order.delivery_method || '',
            '{11}': String(order.total_pieces || ''),
            '{12}': String(order.collection_amount || ''),
            '{13}': String(order.cash_payment || ''),
            '{14}': String(order.advance_transfer_fee || ''),
            '{15}': (() => {
              const currentHideCodAmount = hideCodAmountRef.current;
              const finalValue = getFinalCodAmount(order.cod_total, currentHideCodAmount);
              console.log(
                `🔍 占位符{15}到付金额计算: hideCodAmount=${currentHideCodAmount}, cod_total=${order.cod_total}, finalValue=${finalValue}`
              );
              return finalValue;
            })(),
            '{16}': String(order.receive_remark || ''),
            '{17}': systemSettings?.company_remark || ''
          };

          // 执行替换
          Object.keys(replacements).forEach(key => {
            printContent = printContent.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replacements[key]);
          });

          console.log('📤 最终打印内容:', printContent);
          console.log('🌐 调用API:', apiEndpoint);
          console.log('请求参数:', {
            orderData: order,
            deviceSn: device.device_sn,
            template: printContent
          });

          if (isDevSkipPrintApi) {
            console.log('🧪 调试模式：跳过云打印接口调用', {
              order_no: order.order_no,
              hideCodAmount: hideCodAmountRef.current,
              cod_total: order.cod_total,
              finalCodAmount: replacements['{15}']
            });
            continue;
          }

          // 调用真正的打印API
          const response = await fetch(apiEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              orderData: order,
              deviceSn: device.device_sn,
              template: printContent
            }),
          });

          const result = await response.json();
          console.log('📥 API响应结果:', result);

          if (result.success) {
            console.log(`✅ 第${i + 1}个物流单打印成功！`);
            messageApi.success(`成功打印第${i + 1}/${ordersToPrint.length}物流单`);
          } else {
            console.log(`❌ 第${i + 1}个物流单打印失败:`, result.message);
            messageApi.error(`打印失败: ${result.message}`);
          }
        } else {
          console.log('开始云标签机打印');
          // 云标签机打印逻辑 - 只调用一次，让飞鹅云根据times参数自动打印多张
          const totalPieces = order.total_pieces || 0;
          const printCount = totalPieces > 0 ? totalPieces : 1; // 如果件数为0，至少打印1次
          
          console.log(`总件数: ${totalPieces}, 打印次数: ${printCount}`);
          
          // 替换模板中的占位符（只替换一次，飞鹅云会重复打印）
          let printContent = template;
          const replacements: Record<string, string> = {
            '{0}': order.receiver_name || '',
            '{1}': order.receiver_address || '',
            '{2}': String(totalPieces), // 分母使用总件数
            '{3}': order.order_no || '',
            '{4}': '1' // 这里固定为1，因为飞鹅云会自动处理重复打印
          };

          // 执行替换
          Object.keys(replacements).forEach(key => {
            printContent = printContent.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replacements[key]);
          });

          console.log('📤 云标签打印内容:', printContent);
          console.log('🌐 调用API:', apiEndpoint);
          console.log('请求参数:', {
            orderData: order,
            deviceSn: device.device_sn,
            template: printContent,
            times: printCount // 添加times参数
          });

          if (isDevSkipPrintApi) {
            console.log('🧪 调试模式：跳过云标签接口调用', {
              order_no: order.order_no,
              total_pieces: totalPieces,
              printCount
            });
            continue;
          }

          // 调用真正的打印API
          const response = await fetch(apiEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              orderData: order,
              deviceSn: device.device_sn,
              template: printContent,
              times: printCount // 传递打印次数给后端
            }),
          });

          const result = await response.json();
          console.log('📥 云标签API响应结果:', result);

          if (result.success) {
            console.log(`✅ 第${i + 1}个物流单云标签打印成功！`);
            messageApi.success(`成功打印第${i + 1}/${ordersToPrint.length}物流单云标签`);
          } else {
            console.log(`❌ 第${i + 1}个物流单云标签打印失败:`, result.message);
            messageApi.error(`云标签打印失败: ${result.message}`);
          }
          
        }
        
        // 如果不是最后一个订单，等待1000毫秒
        if (i < ordersToPrint.length - 1) {
          console.log('等待1000毫秒后打印下一个订单...');
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      console.log('批量打印流程结束');
    } catch (error) {
      console.error('❌ 批量打印异常:', error);
      messageApi.error('批量打印失败');
    } finally {
      setPrinting(false);
    }
  }, [messageApi, systemSettings, getFinalCodAmount, isDevSkipPrintApi]);

  // 处理计算字段变化（使用 onChange 事件，完全避免 Form.useWatch 导致的重新渲染）
  // 使用 ref 缓存表单值，避免频繁调用 form.getFieldsValue()
  const formValuesCacheRef = useRef<any>({});
  
  const handleCalculationFieldChange = useCallback((value: number | null, fieldName?: string) => {
    // 立即更新缓存（不触发渲染）
    if (fieldName) {
      formValuesCacheRef.current[fieldName] = value;
    }
    
    // 清除之前的定时器
    if (calculationDebounceRef.current) {
      clearTimeout(calculationDebounceRef.current);
    }

    // 防抖处理：300ms 延迟（增加延迟，减少计算频率）
    calculationDebounceRef.current = setTimeout(() => {
      // 优先使用缓存，只在缓存不完整时才调用 form.getFieldsValue()
      const calculationFields = ['freight', 'delivery_install_fee', 'advance_transfer_fee', 
                                 'collection_amount', 'rebate', 'cash_payment', 'deduction'];
      const cachedValues = { ...formValuesCacheRef.current };
      
      // 检查缓存是否包含所有需要的字段
      const hasAllFields = calculationFields.every(field => 
        cachedValues[field] !== undefined && cachedValues[field] !== null
      );
      
      let currentValues: any;
      if (hasAllFields) {
        // 缓存完整，直接使用缓存
        currentValues = cachedValues;
      } else {
        // 缓存不完整，从表单获取并合并
        const formValues = form.getFieldsValue();
        currentValues = { ...formValues, ...cachedValues };
        // 更新缓存
        calculationFields.forEach(field => {
          if (currentValues[field] !== undefined && currentValues[field] !== null) {
            formValuesCacheRef.current[field] = currentValues[field];
          }
        });
      }
      
      const totalFreight = calculateTotalFreight(currentValues);
      const codTotal = calculateCodTotal(totalFreight, currentValues);
      const payableCollection = calculatePayableCollection(currentValues.collection_amount);
      
      // 处理 NaN 和 Infinity（防止异常值）
      const safeTotalFreight = isNaN(totalFreight) || !isFinite(totalFreight) ? 0 : totalFreight;
      const safeCodTotal = isNaN(codTotal) || !isFinite(codTotal) ? 0 : codTotal;
      const safePayableCollection = isNaN(payableCollection) || !isFinite(payableCollection) ? 0 : payableCollection;

      // 只在值真正变化时更新（避免不必要的重渲染）
      // 使用 Math.abs 比较浮点数，避免精度问题（金额通常保留2位小数）
      const needsUpdate = !lastCalculationRef.current ||
        Math.abs((lastCalculationRef.current.total_freight || 0) - (safeTotalFreight || 0)) > 0.01 ||
        Math.abs((lastCalculationRef.current.cod_total || 0) - (safeCodTotal || 0)) > 0.01 ||
        Math.abs((lastCalculationRef.current.payable_collection || 0) - (safePayableCollection || 0)) > 0.01;

      if (needsUpdate) {
        // 更新状态（会触发延迟更新）
        setCalculatedValues({
          total_freight: safeTotalFreight,
          cod_total: safeCodTotal,
          payable_collection: safePayableCollection,
        });
        
        lastCalculationRef.current = {
          total_freight: safeTotalFreight,
          cod_total: safeCodTotal,
          payable_collection: safePayableCollection,
        };
      }
    }, 300); // 300ms 防抖延迟（增加延迟，减少计算频率）
  }, [form, calculateTotalFreight, calculateCodTotal, calculatePayableCollection]);

  // 缓存发货人列表（避免每次重新过滤和排序）
  const allShippersMemo = useMemo(() => {
    return consigners
      .filter(c => c.subject_type && c.subject_type.includes('发货人'))
      .sort((a, b) => a.id - b.id);
  }, [consigners]);

  // 获取发货人选项（只显示发货人类型）- 使用防抖后的搜索值
  const getShipperOptions = useCallback((inputValue: string, currentValue?: number): SelectOption[] => {
    if (!inputValue) {
      return allShippersMemo.map(consigner => ({
        label: `${consigner.name} (${consigner.phone})`,
        value: consigner.id,
        displayName: `${consigner.name} (${consigner.phone})`,
      }));
    }
    
    const lowerInput = inputValue.toLowerCase();
    const filtered = allShippersMemo.filter(c => 
      c.name.toLowerCase().includes(lowerInput) ||
      (c.phone && c.phone.includes(inputValue))
    );
    
    const options = filtered.map(consigner => ({
      label: `${consigner.name} (${consigner.phone})`,
      value: consigner.id,
      displayName: `${consigner.name} (${consigner.phone})`,
    }));
    
    // 确保当前选中的值始终在 options 中（避免显示 ID）
    if (currentValue && currentValue !== -1) {
      const currentShipper = allShippersMemo.find(c => c.id === currentValue);
      if (currentShipper && !filtered.some(c => c.id === currentValue)) {
        // 如果当前选中的值不在过滤结果中，将其添加到 options 开头
        options.unshift({
          label: `${currentShipper.name} (${currentShipper.phone})`,
          value: currentShipper.id,
          displayName: `${currentShipper.name} (${currentShipper.phone})`,
        });
      }
    }
    
    // 检查是否有完全匹配的选项
    const hasExactMatch = filtered.some(c => c.name === inputValue);
    
    // 如果没有完全匹配且输入不为空，添加新增选项
    if (!hasExactMatch && inputValue.trim()) {
      options.push({
        label: `${inputValue.trim()}[+新增]`, // 下拉选项显示[+新增]
        value: -1, // 使用-1表示新增选项
        isNew: true,
        newName: inputValue.trim(),
        displayName: inputValue.trim() // 选择后显示的名称
      } as SelectOption);
    }

    return options;
  }, [allShippersMemo]);

  // 缓存收货人列表（避免每次重新过滤和排序）
  const allReceiversMemo = useMemo(() => {
    return consigners
      .filter(c => c.subject_type && c.subject_type.includes('收货人'))
      .sort((a, b) => a.id - b.id);
  }, [consigners]);

  // 获取收货人选项（只显示收货人类型）- 使用防抖后的搜索值
  const getReceiverOptions = useCallback((inputValue: string, currentValue?: number): SelectOption[] => {
    if (!inputValue) {
      return allReceiversMemo.map(consigner => ({
        label: `${consigner.name} (${consigner.phone})`,
        value: consigner.id,
        displayName: `${consigner.name} (${consigner.phone})`,
      }));
    }
    
    const lowerInput = inputValue.toLowerCase();
    const filtered = allReceiversMemo.filter(c => 
      c.name.toLowerCase().includes(lowerInput) ||
      (c.phone && c.phone.includes(inputValue))
    );
    
    const options = filtered.map(consigner => ({
      label: `${consigner.name} (${consigner.phone})`,
      value: consigner.id,
      displayName: `${consigner.name} (${consigner.phone})`,
    }));
    
    // 确保当前选中的值始终在 options 中（避免显示 ID）
    if (currentValue && currentValue !== -1) {
      const currentReceiver = allReceiversMemo.find(c => c.id === currentValue);
      if (currentReceiver && !filtered.some(c => c.id === currentValue)) {
        // 如果当前选中的值不在过滤结果中，将其添加到 options 开头
        options.unshift({
          label: `${currentReceiver.name} (${currentReceiver.phone})`,
          value: currentReceiver.id,
          displayName: `${currentReceiver.name} (${currentReceiver.phone})`,
        });
      }
    }
    
    // 检查是否有完全匹配的选项
    const hasExactMatch = filtered.some(c => c.name === inputValue);
    
    // 如果没有完全匹配且输入不为空，添加新增选项
    if (!hasExactMatch && inputValue.trim()) {
      options.push({
        label: `${inputValue.trim()}[+新增]`, // 下拉选项显示[+新增]
        value: -1, // 使用-1表示新增选项
        isNew: true,
        newName: inputValue.trim(),
        displayName: inputValue.trim() // 选择后显示的名称
      } as SelectOption);
    }

    return options;
  }, [allReceiversMemo]);

  // 导出Excel文件（移至组件底部，依赖filteredOrders等）
  const handleExportExcel = useCallback(async () => { /* placeholder, real impl after dependencies */ }, []);

  // 导出CSV文件
  // 导出CSV（移至组件底部，依赖filteredOrders等）
  const handleExportCSV = useCallback(async () => { /* placeholder, real impl after dependencies */ }, []);

  // 文件上传处理
  const handleFileUpload = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let data: any[] = [];
        
        if (file.name.endsWith('.csv')) {
          // 处理CSV文件
          const text = e.target?.result as string;
          const lines = text.split('\n');
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          data = lines.slice(1).filter(line => line.trim()).map((line, index) => {
            const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
            const row: any = {};
            headers.forEach((header, i) => {
              row[header] = values[i] || '';
            });
            row._rowIndex = index + 1;
            return row;
          });
        } else {
          // 处理Excel文件
          const workbook = XLSX.read(e.target?.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length > 1) {
            const headers = jsonData[0] as string[];
            data = jsonData.slice(1).map((row: any, index: number) => {
              const rowData: any = {};
              headers.forEach((header, i) => {
                rowData[header] = row[i] || '';
              });
              rowData._rowIndex = index + 1;
              return rowData;
            });
          }
        }

        // 根据字段名称智能推断映射关系
        const defaultMapping: Record<string, string> = {};
        const usedTargetFields = new Set<string>();
        
        Object.keys(data[0] || {}).forEach(header => {
          if (header === '_rowIndex') return;
          
          const headerLower = header.toLowerCase();
          let targetField = '';
          
          // 物流单号 - 最重要的字段
          if (headerLower.includes('物流单号') || headerLower.includes('单号') || headerLower.includes('订单号')) {
            targetField = '物流单号';
          }
          // 收货日期
          else if (headerLower.includes('收货日期') || headerLower.includes('日期') || headerLower.includes('接收日期')) {
            targetField = '收货日期';
          }
          // 发货人相关
          else if (headerLower.includes('发货人') || headerLower.includes('shipper')) {
            if (headerLower.includes('姓名') || headerLower.includes('name')) {
              targetField = '发货人姓名';
            } else if (headerLower.includes('电话') || headerLower.includes('phone')) {
              targetField = '发货人电话';
            }
          }
          // 收货人相关
          else if (headerLower.includes('收货人') || headerLower.includes('receiver')) {
            if (headerLower.includes('姓名') || headerLower.includes('name')) {
              targetField = '收货人姓名';
            } else if (headerLower.includes('电话') || headerLower.includes('phone')) {
              targetField = '收货人电话';
            } else if (headerLower.includes('地址') || headerLower.includes('address')) {
              targetField = '收货人地址';
            }
          }
          // 装货区
          else if (headerLower.includes('装货') || headerLower.includes('loading') || headerLower.includes('装货区')) {
            targetField = '装货区';
          }
          // 卸货站点
          else if (headerLower.includes('卸货') || headerLower.includes('unloading') || headerLower.includes('卸货站点')) {
            targetField = '卸货站点';
          }
          // 货物名称
          else if (headerLower.includes('货物') || headerLower.includes('goods') || headerLower.includes('货品')) {
            targetField = '货物名称';
          }
          // 总件数
          else if (headerLower.includes('件数') || headerLower.includes('pieces') || headerLower.includes('总件数')) {
            targetField = '总件数';
          }
          // 运费
          else if (headerLower.includes('运费') || headerLower.includes('freight')) {
            targetField = '运费';
          }
          // 送货安装费
          else if (headerLower.includes('送货安装') || headerLower.includes('install') || headerLower.includes('安装费')) {
            targetField = '送货安装费';
          }
          // 垫付费
          else if (headerLower.includes('垫付') || headerLower.includes('advance') || headerLower.includes('预转费')) {
            targetField = '垫付费';
          }
          // 代收款
          else if (headerLower.includes('代收') || headerLower.includes('collection') || headerLower.includes('代收款')) {
            targetField = '代收款';
          }
          // 结算方式
          else if (headerLower.includes('结算') || headerLower.includes('settlement') || headerLower.includes('结算方式')) {
            targetField = '结算方式';
          }
          // 送货方式
          else if (headerLower.includes('送货方式') || headerLower.includes('delivery') || headerLower.includes('配送方式')) {
            targetField = '送货方式';
          }
          // 业务归属
          else if (headerLower.includes('业务归属') || headerLower.includes('业务员') || headerLower.includes('负责人')) {
            targetField = '业务归属';
          }
          // 收货备注
          else if (headerLower.includes('备注') || headerLower.includes('remark') || headerLower.includes('收货备注')) {
            targetField = '收货备注';
          }
          // 状态
          else if (headerLower.includes('状态') || headerLower.includes('status')) {
            targetField = '状态';
          }
          
          if (targetField && usedTargetFields.has(targetField)) {
            targetField = '';
          }
          
          if (targetField) {
            defaultMapping[header] = targetField;
            usedTargetFields.add(targetField);
          } else {
            defaultMapping[header] = '';
          }
        });

        setFieldMapping(defaultMapping);
        setImportData(data);
        setImportModalVisible(true);
      } catch (error) {
        messageApi.error('文件解析失败');
      }
    };
    
    if (file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
    
    return false;
  }, []);

  // 执行导入
  const handleImport = useCallback(async () => {
    try {
      setImporting(true);
      
      // 映射数据 - 将中文字段名转换为后端字段名
      const fieldNameMapping: Record<string, string> = {
        '物流单号': 'order_no',
        '收货日期': 'receive_date',
        '发货人姓名': 'shipper_name',
        '发货人电话': 'shipper_phone',
        '装货区': 'loading_area',
        '卸货站点': 'unloading_station',
        '收货人姓名': 'receiver_name',
        '收货人电话': 'receiver_phone',
        '收货人地址': 'receiver_address',
        '货物名称': 'goods_name',
        '总件数': 'total_pieces',
        '运费': 'freight',
        '送货安装费': 'delivery_install_fee',
        '垫付费': 'advance_transfer_fee',
        '代收款': 'collection_amount',
        '结算方式': 'settlement_method',
        '送货方式': 'delivery_method',
        '业务归属': 'business_owner_name',
        '收货备注': 'receive_remark',
        '状态': 'status'
      };

      const mappedData = importData.map((row, index) => {
        const mapped: any = {};
        Object.keys(fieldMapping).forEach(sourceField => {
          const targetField = fieldMapping[sourceField];
          if (targetField && targetField !== 'skip') {
            // 将中文字段名转换为后端字段名
            const backendFieldName = fieldNameMapping[targetField] || targetField;
            mapped[backendFieldName] = row[sourceField];
          }
        });
        mapped._rowIndex = index + 1;
        return mapped;
      });

      if (mappedData.length === 0) {
        messageApi.error('没有数据可以导入');
        return;
      }

      // 调用后端批量导入API
      const response = await fetch('/api/logistics-orders/batch-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: mappedData }),
      });

      const result = await response.json();
      
      if (result.success) {
        setImportResult(result);
        messageApi.success(`导入完成！成功: ${result.successCount} 条`);
        
        // 刷新数据列表
        setTimeout(() => {
          fetchOrders();
        }, 500);
      } else {
        messageApi.error(result.message || '导入失败');
      }
    } catch (error) {
      console.error('导入过程中发生错误:', error);
              messageApi.error('导入失败');
    } finally {
      setImporting(false);
    }
  }, [fieldMapping, importData, fetchOrders]);

  // 计算表格总宽度（所有列宽度之和，确保固定列对齐）
  const tableScrollX = useMemo(() => {
    // 所有列的宽度总和（包括固定列）
    const columnWidths = [
      120, // 收货人 (fixed: left)
      130, // 收货人电话
      150, // 货物名称
      90,  // 总件数
      110, // 运费
      130, // 送货安装费
      130, // 垫付中转费
      120, // 代收货款
      100, // 返点
      110, // 现付款
      120, // 到付金额
      220, // 收货地址
      150, // 物流单号
      160, // 收货日期
      120, // 装货区域
      120, // 发货人
      130, // 发货人电话
      120, // 卸货区域
      120, // 送货方式
      120, // 结算方式
      180, // 备注
      120, // 已收款
      120, // 已付款
      110, // 来往确认
      250, // 操作 (fixed: right) - 增加宽度以容纳三个按钮（查看、编辑、删除）
    ];
    const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
    // 当有固定列时，需要确保宽度足够，添加一些额外宽度以确保对齐
    return totalWidth + 50; // 添加 50px 的额外宽度，确保固定列对齐
  }, []);

  // 表格列定义（按用户期望顺序）- 使用 useMemo 缓存，避免每次渲染都重新创建
  const columns = useMemo(() => [
    { title: '收货人', dataIndex: 'receiver_name', key: 'receiver_name', width: 120, fixed: 'left' as const, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.receiver_name || '').localeCompare(b.receiver_name || '') },
    { title: '收货人电话', dataIndex: 'receiver_phone', key: 'receiver_phone', width: 130 },
    { title: '货物名称', dataIndex: 'goods_name', key: 'goods_name', width: 150, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.goods_name || '').localeCompare(b.goods_name || '') },
    { title: '总件数', dataIndex: 'total_pieces', key: 'total_pieces', width: 90, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.total_pieces || 0) - (b.total_pieces || 0) },
    { title: '运费', dataIndex: 'freight', key: 'freight', width: 110, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.freight || 0) - (b.freight || 0), render: (v: any) => `¥${formatCurrency(v)}` },
    { title: '送货安装费', dataIndex: 'delivery_install_fee', key: 'delivery_install_fee', width: 130, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.delivery_install_fee || 0) - (b.delivery_install_fee || 0), render: (v: any) => `¥${formatCurrency(v)}` },
    { title: '垫付中转费', dataIndex: 'advance_transfer_fee', key: 'advance_transfer_fee', width: 130, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.advance_transfer_fee || 0) - (b.advance_transfer_fee || 0), render: (v: any) => `¥${formatCurrency(v)}` },
    { title: '代收货款', dataIndex: 'collection_amount', key: 'collection_amount', width: 120, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.collection_amount || 0) - (b.collection_amount || 0), render: (v: any) => `¥${formatCurrency(v)}` },
    { title: '返点', dataIndex: 'rebate', key: 'rebate', width: 100, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.rebate || 0) - (b.rebate || 0), render: (v: any) => `¥${formatCurrency(v)}` },
    { title: '现付款', dataIndex: 'cash_payment', key: 'cash_payment', width: 110, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.cash_payment || 0) - (b.cash_payment || 0), render: (v: any) => `¥${formatCurrency(v)}` },
    { title: '到付金额', dataIndex: 'cod_total', key: 'cod_total', width: 120, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.cod_total || 0) - (b.cod_total || 0), render: (v: any) => `¥${formatCurrency(v)}` },
    { title: '收货地址', dataIndex: 'receiver_address', key: 'receiver_address', width: 220 },
    { title: '物流单号', dataIndex: 'order_no', key: 'order_no', width: 150, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.order_no || '').localeCompare(b.order_no || '') },
    { title: '收货日期', dataIndex: 'receive_date', key: 'receive_date', width: 160, sorter: (a: LogisticsOrder, b: LogisticsOrder) => new Date(a.receive_date || 0).getTime() - new Date(b.receive_date || 0).getTime(), render: (text: string) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-' },
    { title: '装货区域', dataIndex: 'loading_area', key: 'loading_area', width: 120, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.loading_area || '').localeCompare(b.loading_area || '') },
    { title: '发货人', dataIndex: 'shipper_name', key: 'shipper_name', width: 120, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.shipper_name || '').localeCompare(b.shipper_name || '') },
    { title: '发货人电话', dataIndex: 'shipper_phone', key: 'shipper_phone', width: 130 },
    { title: '卸货区域', dataIndex: 'unloading_station', key: 'unloading_station', width: 120, sorter: (a: LogisticsOrder, b: LogisticsOrder) => (a.unloading_station || '').localeCompare(b.unloading_station || '') },
    { title: '送货方式', dataIndex: 'delivery_method', key: 'delivery_method', width: 120 },
    { title: '结算方式', dataIndex: 'settlement_method', key: 'settlement_method', width: 120 },
    { title: '备注', dataIndex: 'receive_remark', key: 'receive_remark', width: 180, ellipsis: true },
    { title: '已收款', dataIndex: 'received_amount', key: 'received_amount', width: 120, render: (v: any) => v !== undefined ? `¥${formatCurrency(v)}` : '-' },
    { title: '已付款', dataIndex: 'paid_amount', key: 'paid_amount', width: 120, render: (v: any) => v !== undefined ? `¥${formatCurrency(v)}` : '-' },
    { title: '来往确认', dataIndex: 'reconciliation_status', key: 'reconciliation_status', width: 110, render: (v: any) => v ?? '-' },

    { title: '操作', key: 'action', width: 250, fixed: 'right' as const, render: (_: any, record: LogisticsOrder) => (
        <Space size="small" wrap={false}>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => showDrawer(record)}>查看</Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => showModal(record)}>编辑</Button>
          <Popconfirm title="确定要删除这个物流单吗？" onConfirm={() => handleDelete(record.id!)} okText="确定" cancelText="取消">
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ) },
  ], [formatCurrency, showDrawer, showModal, handleDelete]);

  // 移动端卡片渲染
  const renderOrderCard = useCallback((order: LogisticsOrder) => {
    const isSelected = selectedRowKeys.includes(order.id!);
    
    return (
    <Card
      key={order.id}
      size="small"
      style={{ 
        marginBottom: 16,
        border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
        backgroundColor: isSelected ? '#f0f8ff' : '#fff',
        transition: 'all 0.3s ease',
        cursor: 'pointer'
      }}
      onClick={() => {
        // 切换选择状态
        const newSelectedKeys = isSelected 
          ? selectedRowKeys.filter(key => key !== order.id)
          : [...selectedRowKeys, order.id!];
        
        const newSelectedRows = isSelected
          ? selectedRows.filter(row => row.id !== order.id)
          : [...selectedRows, order];
        
        handleRowSelectionChange(newSelectedKeys, newSelectedRows);
      }}
      actions={[
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            showDrawer(order);
          }}
        >
          查看
        </Button>,
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            showModal(order);
          }}
        >
          编辑
        </Button>,
        <Popconfirm
          title="确定要删除这个物流单吗？"
          onConfirm={() => handleDelete(order.id!)}
          okText="确定"
          cancelText="取消"
        >
          <Button 
            type="link" 
            size="small" 
            danger 
            icon={<DeleteOutlined />}
            onClick={(e) => e.stopPropagation()}
          >
            删除
          </Button>
        </Popconfirm>,
      ]}
    >
      <div style={{ 
        marginBottom: 8, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <Text strong>{order.order_no}</Text>
      </div>
      <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">发货人：</Text>
        <Text style={{ textAlign: 'right' }}>{order.shipper_name}</Text>
      </div>
      <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">收货人：</Text>
        <Text style={{ textAlign: 'right' }}>{order.receiver_name}</Text>
      </div>
      <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">货品：</Text>
        <Text style={{ textAlign: 'right' }}>{order.goods_name}</Text>
      </div>
      <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">总件数：</Text>
        <Text style={{ textAlign: 'right' }}>{order.total_pieces ?? 0}</Text>
      </div>
      <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">总运费：</Text>
        <Text strong style={{ textAlign: 'right' }}>¥{formatCurrency(order.total_freight)}</Text>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">收货日期：</Text>
        <Text style={{ textAlign: 'right' }}>{order.receive_date ? dayjs(order.receive_date).format('YYYY-MM-DD HH:mm:ss') : '-'}</Text>
      </div>
    </Card>
    );
  }, [showDrawer, showModal, handleDelete, selectedRowKeys, selectedRows, handleRowSelectionChange]);

  // 过滤后的数据（现在通过API搜索，这里主要用于移动端显示）
  const filteredOrders = useMemo(() => {
    return orders;
  }, [orders]);

  // 应用与表格一致的排序（仅在导出时使用）
  const applyCurrentSort = useCallback((data: LogisticsOrder[]): LogisticsOrder[] => {
    if (!tableSorter?.field || !tableSorter.order) return data;
    const field = tableSorter.field as keyof LogisticsOrder;
    const orderFactor = tableSorter.order === 'ascend' ? 1 : -1;
    const numericFields = new Set([
      'total_pieces',
      'freight',
      'delivery_install_fee',
      'advance_transfer_fee',
      'collection_amount',
      'rebate',
      'cash_payment',
      'cod_total'
    ]);
    const dateFields = new Set(['receive_date']);
    return [...data].sort((a: any, b: any) => {
      const av = a[field];
      const bv = b[field];
      if (dateFields.has(field as string)) {
        const at = av ? new Date(av as any).getTime() : 0;
        const bt = bv ? new Date(bv as any).getTime() : 0;
        return (at - bt) * orderFactor;
      }
      if (numericFields.has(field as string)) {
        const an = Number(av || 0);
        const bn = Number(bv || 0);
        return (an - bn) * orderFactor;
      }
      const as = (av || '').toString();
      const bs = (bv || '').toString();
      return as.localeCompare(bs) * orderFactor;
    });
  }, [tableSorter]);

  // 计算当前分页的合计
  const calculatePageSummary = useCallback(() => {
    const summary = {
      freight: 0,
      delivery_install_fee: 0,
      advance_transfer_fee: 0,
      collection_amount: 0,
      rebate: 0,
      cash_payment: 0,
      cod_total: 0,
    };

    filteredOrders.forEach(order => {
      summary.freight += Number(order.freight || 0);
      summary.delivery_install_fee += Number(order.delivery_install_fee || 0);
      summary.advance_transfer_fee += Number(order.advance_transfer_fee || 0);
      summary.collection_amount += Number(order.collection_amount || 0);
      summary.rebate += Number(order.rebate || 0);
      summary.cash_payment += Number(order.cash_payment || 0);
      summary.cod_total += Number(order.cod_total || 0);
    });

    return summary;
  }, [filteredOrders]);

  // 重新实现：导出Excel（使用当前分页与排序，包含合计）
  const exportExcelWithCurrentPage = useCallback(async () => {
    setExporting(true);
    try {
      const currentPageData = applyCurrentSort(filteredOrders);
      const headersExcel = [
        '收货人','收货人电话','货物名称','总件数','运费','送货安装费','垫付中转费','代收货款','返点','现付款','到付金额','收货地址','物流单号','收货日期','装货区域','发货人','发货人电话','卸货区域','送货方式','结算方式','备注','已收款','已付款','来往确认'
      ];
      const exportRows = currentPageData.map((order: LogisticsOrder) => [
        order.receiver_name,
        order.receiver_phone,
        order.goods_name,
        Number(order.total_pieces ?? 0),
        Number(order.freight ?? 0),
        Number(order.delivery_install_fee ?? 0),
        Number(order.advance_transfer_fee ?? 0),
        Number(order.collection_amount ?? 0),
        Number(order.rebate ?? 0),
        Number(order.cash_payment ?? 0),
        Number(order.cod_total ?? 0),
        order.receiver_address,
        order.order_no,
        order.receive_date ? new Date(order.receive_date) : '',
        order.loading_area,
        order.shipper_name,
        order.shipper_phone,
        order.unloading_station,
        order.delivery_method,
        order.settlement_method,
        order.receive_remark,
        (order as any).received_amount ?? '',
        (order as any).paid_amount ?? '',
        (order as any).reconciliation_status ?? ''
      ]);
      const pageSummary = calculatePageSummary();
      const summaryRow = [
        '合计', // 收货人
        '', // 收货人电话
        '', // 货物名称
        '', // 总件数
        Number(pageSummary.freight),
        Number(pageSummary.delivery_install_fee),
        Number(pageSummary.advance_transfer_fee),
        Number(pageSummary.collection_amount),
        Number(pageSummary.rebate),
        Number(pageSummary.cash_payment),
        Number(pageSummary.cod_total),
        '', // 收货地址
        '', // 物流单号
        '', // 收货日期
        '', // 装货区域
        '', // 发货人
        '', // 发货人电话
        '', // 卸货区域
        '', // 送货方式
        '', // 结算方式
        '', // 备注
        '', // 已收款
        '', // 已付款
        '', // 来往确认
      ];
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet([headersExcel, ...exportRows, summaryRow]);
      // 设置金额列为数值格式(0.00)，收货日期为日期格式
      const amountCols = [4,5,6,7,8,9,10]; // 0-based 索引
      const dateCol = 13; // 收货日期
      const startRow = 2; // 首行是表头，数据从第2行开始（1-based）
      const totalRows = exportRows.length + 1; // 含表头
      const XLSX_COL = (n: number) => {
        let s = '';
        while (n >= 0) { s = String.fromCharCode((n % 26) + 65) + s; n = Math.floor(n / 26) - 1; }
        return s;
      };
      // 金额列设为数值类型（不强制 0.00 显示格式）
      for (const c of amountCols) {
        for (let r = 1; r <= totalRows; r++) { // 包含汇总行
          const addr = `${XLSX_COL(c)}${r+1}`; // +1 因为表头占第1行
          const cell = (worksheet as any)[addr];
          if (cell && typeof cell.v === 'number') {
            cell.t = 'n';
          }
        }
      }
      // 收货日期列设置为日期
      for (let r = 1; r <= exportRows.length; r++) { // 仅数据行
        const addr = `${XLSX_COL(dateCol)}${r+1}`;
        const cell = (worksheet as any)[addr];
        if (cell && cell.v instanceof Date) {
          cell.t = 'd';
          cell.z = 'yyyy-mm-dd hh:mm:ss';
        }
      }
      const colWidths = [
        { wch: 12 },{ wch: 15 },{ wch: 20 },{ wch: 8 },{ wch: 12 },{ wch: 14 },{ wch: 14 },{ wch: 12 },{ wch: 10 },{ wch: 12 },{ wch: 12 },{ wch: 28 },{ wch: 16 },{ wch: 20 },{ wch: 12 },{ wch: 12 },{ wch: 15 },{ wch: 12 },{ wch: 12 },{ wch: 12 },{ wch: 16 },{ wch: 12 },{ wch: 12 },{ wch: 12 }
      ];
      (worksheet as any)['!cols'] = colWidths;
      XLSX.utils.book_append_sheet(workbook, worksheet, '物流单');
      XLSX.writeFile(workbook, `物流单列表_${new Date().toISOString().split('T')[0]}.xlsx`);
      messageApi.success('Excel文件导出成功');
    } catch (error) {
      console.error('导出Excel失败:', error);
      messageApi.error('Excel文件导出失败');
    } finally {
      setExporting(false);
    }
  }, [filteredOrders, applyCurrentSort, calculatePageSummary, messageApi]);

  // 重新实现：导出CSV（使用当前分页与排序，包含合计）
  const exportCSVWithCurrentPage = useCallback(async () => {
    setExporting(true);
    try {
      const currentPageData = applyCurrentSort(filteredOrders);
      const headersCsv = [
        '收货人','收货人电话','货物名称','总件数','运费','送货安装费','垫付中转费','代收货款','返点','现付款','到付金额','收货地址','物流单号','收货日期','装货区域','发货人','发货人电话','卸货区域','送货方式','结算方式','备注','已收款','已付款','来往确认'
      ];
      const csvRows = currentPageData.map((order: LogisticsOrder) => ([
        order.receiver_name,
        order.receiver_phone,
        order.goods_name,
        order.total_pieces,
        formatCurrency(order.freight),
        formatCurrency(order.delivery_install_fee),
        formatCurrency(order.advance_transfer_fee),
        formatCurrency(order.collection_amount),
        formatCurrency(order.rebate),
        formatCurrency(order.cash_payment),
        formatCurrency(order.cod_total),
        (order.receiver_address || '').replace(/\n/g, ' '),
        order.order_no,
        order.receive_date ? dayjs(order.receive_date).format('YYYY-MM-DD HH:mm:ss') : '',
        order.loading_area,
        order.shipper_name,
        order.shipper_phone,
        order.unloading_station,
        order.delivery_method,
        order.settlement_method,
        (order.receive_remark || '').replace(/\n/g, ' '),
        (order as any).received_amount ?? '',
        (order as any).paid_amount ?? '',
        (order as any).reconciliation_status ?? ''
      ]));
      const pageSummary = calculatePageSummary();
      const summaryRow = [
        '合计', // 收货人
        '', // 收货人电话
        '', // 货物名称
        '', // 总件数
        formatCurrency(pageSummary.freight),
        formatCurrency(pageSummary.delivery_install_fee),
        formatCurrency(pageSummary.advance_transfer_fee),
        formatCurrency(pageSummary.collection_amount),
        formatCurrency(pageSummary.rebate),
        formatCurrency(pageSummary.cash_payment),
        formatCurrency(pageSummary.cod_total),
        '', // 收货地址
        '', // 物流单号
        '', // 收货日期
        '', // 装货区域
        '', // 发货人
        '', // 发货人电话
        '', // 卸货区域
        '', // 送货方式
        '', // 结算方式
        '', // 备注
        '', // 已收款
        '', // 已付款
        '', // 来往确认
      ];
      const csvContent = [
        headersCsv.join(','),
        ...csvRows.map((row) => row.map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')),
        summaryRow.map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')
      ].join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `物流单列表_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      messageApi.success('CSV文件导出成功');
    } catch (error) {
      console.error('导出CSV失败:', error);
      messageApi.error('CSV文件导出失败');
    } finally {
      setExporting(false);
    }
  }, [filteredOrders, applyCurrentSort, calculatePageSummary, messageApi]);

  return (
    <div style={{ padding: isMobile ? '16px' : '12px' }}>
      {contextHolder}
      <Card>
        <div style={{ marginBottom: 16 }}>
                    <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={24} md={3}>
              <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>物流单管理</Title>
            </Col>
            <Col xs={24} sm={24} md={21}>
              <Row gutter={[8, 8]} align="middle">
                <Col xs={24} sm={12} md={6}>
                  <Input
                    key={searchInputKey}
                    placeholder="搜索物流单号、发货人、收货人、电话、卸货站点"
                    prefix={<SearchOutlined />}
                    ref={searchInputRef}
                    defaultValue={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    onPressEnter={() => {
                      // 立即执行搜索，清除防抖定时器
                      if (debouncedSearchRef.current) {
                        clearTimeout(debouncedSearchRef.current);
                      }
                      const finalValue = searchInputRef.current?.input?.value || searchValueRef.current;
                      searchValueRef.current = finalValue;
                      setSearchText(finalValue);
                      setCurrentPage(1);
                      // 检测输入内容，自动设置时间筛选复选框状态
                      if (finalValue.trim()) {
                        const isPureAlphanumeric = /^[a-zA-Z0-9]+$/.test(finalValue.trim());
                        const hasChinese = /[\u4e00-\u9fa5]/.test(finalValue);
                        if (isPureAlphanumeric) {
                          setUseTimeFilter(false);
                        } else if (hasChinese) {
                          setUseTimeFilter(true);
                        }
                      } else {
                        setUseTimeFilter(true);
                      }
                      fetchOrders(finalValue);
                    }}
                    onClear={() => {
                      searchValueRef.current = '';
                      setSearchText('');
                      setCurrentPage(1);
                      // 清空搜索框时，恢复时间筛选为默认选中
                      setUseTimeFilter(true);
                      fetchOrders('');
                    }}
                    style={{ width: '100%' }}
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={12} md={3}>
                  <Select
                    placeholder="选择结算方式"
                    value={settlementMethod}
                    onChange={(value) => {
                      setSettlementMethod(value);
                      handleAdvancedSearchChange();
                    }}
                    allowClear
                    style={{ width: '100%' }}
                    options={settlementMethodOptions}
                  />
                </Col>
                <Col xs={24} sm={12} md={3} style={{ paddingRight: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8 }}>
                    <Checkbox
                      checked={useTimeFilter}
                      onChange={(e) => {
                        setUseTimeFilter(e.target.checked);
                        // 复选框变化时触发搜索
                        setTimeout(() => {
                          handleAdvancedSearchChange();
                        }, 0);
                      }}
                    >
                      时间
                    </Checkbox>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={4} style={{ paddingLeft: 0 }}>
                  <DatePicker
                    placeholder="开始日期"
                    value={startDate}
                    onChange={(date) => {
                      setStartDate(date || dayjs());
                      handleAdvancedSearchChange();
                    }}
                    format="YYYY-MM-DD"
                    style={{ width: '100%' }}
                    disabled={!useTimeFilter}
                  />
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <DatePicker
                    placeholder="结束日期"
                    value={endDate}
                    onChange={(date) => {
                      setEndDate(date || dayjs());
                      handleAdvancedSearchChange();
                    }}
                    format="YYYY-MM-DD"
                    style={{ width: '100%' }}
                    disabled={!useTimeFilter}
                  />
                </Col>

                <Col xs={24} sm={12} md={2}>
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={handleRefresh}
                    style={{ width: '100%' }}
                  >
                    刷新
                  </Button>
                </Col>
                <Col xs={24} sm={12} md={2}>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          label: '导出Excel',
                          key: 'export-excel',
                          icon: <DownloadOutlined />,
                        },
                        {
                          label: '导出CSV',
                          key: 'export-csv',
                          icon: <DownloadOutlined />,
                        },
                      ],
                      onClick: ({ key }) => {
                        if (key === 'export-excel') {
                          exportExcelWithCurrentPage();
                        } else if (key === 'export-csv') {
                          exportCSVWithCurrentPage();
                        }
                      },
                    }}
                  >
                    <Button icon={<DownloadOutlined />} loading={exporting} style={{ width: '100%' }}>
                      导出
                    </Button>
                  </Dropdown>
                </Col>
                <Col xs={24} sm={12} md={2} style={{ display: 'none' }}>
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() => setImportModalVisible(true)}
                    style={{ width: '100%' }}
                  >
                    导入
                  </Button>
                </Col>
                {isMobile && (
                  <Col xs={24} sm={12} md={4}>
                    <Button 
                      icon={<PlusOutlined />} 
                      onClick={() => showModal()}
                      type="primary"
                      style={{ width: '100%' }}
                    >
                      新增
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          
          {isMobile && (
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                共 {total} 个物流单（当前页显示 {filteredOrders.length} 个）
              </Text>
            </div>
          )}
        </div>

      {/* 数据展示 */}
      {isMobile ? (
        // 移动端卡片布局
        <div>
          {/* 移动端打印按钮 */}
          <div style={{ marginBottom: 16 }}>
            <Row gutter={[8, 8]}>
              <Col xs={24} sm={8} md={4}>
                <Checkbox 
                  checked={hideCodAmount}
                  onChange={(e) => {
                    console.log('🔘 复选框状态变化:', e.target.checked);
                    setHideCodAmount(e.target.checked);
                  }}
                  style={{ marginRight: 8 }}
                >
                  隐藏到付金额
                </Checkbox>
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Button 
                  icon={<PrinterOutlined />} 
                  onClick={() => handlePrintClick('receipt', EAST_PRINTER_SN)}
                  disabled={selectedRows.length === 0}
                  style={{ 
                    width: '100%',
                    backgroundColor: selectedRows.length === 0 ? undefined : '#1890ff',
                    borderColor: selectedRows.length === 0 ? undefined : '#1890ff',
                    color: selectedRows.length === 0 ? undefined : 'white'
                  }}
                >
                  云打印（东）
                </Button>
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Button 
                  icon={<PrinterOutlined />} 
                  onClick={() => handlePrintClick('receipt', WEST_PRINTER_SN)}
                  disabled={selectedRows.length === 0}
                  style={{ 
                    width: '100%',
                    backgroundColor: selectedRows.length === 0 ? undefined : '#1677ff',
                    borderColor: selectedRows.length === 0 ? undefined : '#1677ff',
                    color: selectedRows.length === 0 ? undefined : 'white'
                  }}
                >
                  云打印（西）
                </Button>
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Button 
                  icon={<TagsOutlined />} 
                  onClick={() => handlePrintClick('label')}
                  disabled={selectedRows.length === 0}
                  style={{ 
                    width: '100%',
                    backgroundColor: selectedRows.length === 0 ? undefined : '#52c41a',
                    borderColor: selectedRows.length === 0 ? undefined : '#52c41a',
                    color: selectedRows.length === 0 ? undefined : 'white'
                  }}
                >
                  云标签
                </Button>
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Button 
                  icon={<PrinterOutlined />} 
                  onClick={() => handlePrint140Click()}
                  disabled={selectedRows.length === 0}
                  style={{ 
                    width: '100%',
                    backgroundColor: selectedRows.length === 0 ? undefined : '#fa8c16',
                    borderColor: selectedRows.length === 0 ? undefined : '#fa8c16',
                    color: selectedRows.length === 0 ? undefined : 'white'
                  }}
                >
                  140小单
                </Button>
              </Col>
            </Row>
          </div>

          {/* 移动端选择提示和操作 */}
          <div style={{ 
            marginBottom: '12px', 
            padding: '8px 12px', 
            backgroundColor: selectedRows.length > 0 ? '#e6f7ff' : '#f6ffed', 
            border: selectedRows.length > 0 ? '1px solid #91d5ff' : '1px solid #b7eb8f', 
            borderRadius: '6px',
            fontSize: '14px',
            color: selectedRows.length > 0 ? '#1890ff' : '#52c41a'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                {selectedRows.length > 0 ? (
                  `✅ 已选择 ${selectedRows.length} 个物流单，可以使用上方打印按钮`
                ) : (
                  '💡 点击卡片选择物流单，然后使用上方的打印按钮'
                )}
              </span>
              {filteredOrders.length > 0 && (
                <Button 
                  size="small" 
                  type="link"
                  onClick={() => {
                    if (selectedRows.length === filteredOrders.length) {
                      // 取消全选
                      handleRowSelectionChange([], []);
                    } else {
                      // 全选
                      const allKeys = filteredOrders.map(order => order.id!);
                      handleRowSelectionChange(allKeys, filteredOrders);
                    }
                  }}
                  style={{ padding: '0 8px', height: 'auto' }}
                >
                  {selectedRows.length === filteredOrders.length ? '取消全选' : '全选'}
                </Button>
              )}
            </div>
          </div>
          
          {filteredOrders.length > 0 ? (
            filteredOrders.map(renderOrderCard)
          ) : (
            <Empty description="暂无数据" />
          )}
          
          {/* 移动端合计行 */}
          {filteredOrders.length > 0 && (
            <div style={{ 
              marginTop: 16, 
              padding: '12px 16px', 
              backgroundColor: '#fafafa', 
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 'bold'
            }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>当前分页合计</Text>
              </div>
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Text>运费：¥{formatCurrency(calculatePageSummary().freight)}</Text>
                </Col>
                <Col span={12}>
                  <Text>送货安装费：¥{formatCurrency(calculatePageSummary().delivery_install_fee)}</Text>
                </Col>
                <Col span={12}>
                  <Text>垫付中转费：¥{formatCurrency(calculatePageSummary().advance_transfer_fee)}</Text>
                </Col>
                <Col span={12}>
                  <Text>代收货款：¥{formatCurrency(calculatePageSummary().collection_amount)}</Text>
                </Col>
                <Col span={12}>
                  <Text>返点：¥{formatCurrency(calculatePageSummary().rebate)}</Text>
                </Col>
                <Col span={12}>
                  <Text>现付款：¥{formatCurrency(calculatePageSummary().cash_payment)}</Text>
                </Col>
                <Col span={12}>
                  <Text>到付金额：¥{formatCurrency(calculatePageSummary().cod_total)}</Text>
                </Col>
              </Row>
            </div>
          )}
        </div>
      ) : (
        // 桌面端表格布局
        <Card>
          {/* PC模式上方打印按钮 */}
          <div style={{ marginBottom: 16 }}>
            <Row gutter={[8, 8]} justify="start">
              <Col xs={24} sm={8} md={4}>
                <Checkbox 
                  checked={hideCodAmount}
                  onChange={(e) => {
                    console.log('🔘 复选框状态变化:', e.target.checked);
                    setHideCodAmount(e.target.checked);
                  }}
                  style={{ marginRight: 8 }}
                >
                  隐藏到付金额
                </Checkbox>
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Button 
                  icon={<PrinterOutlined />} 
                  onClick={() => handlePrintClick('receipt', EAST_PRINTER_SN)}
                  disabled={selectedRows.length === 0}
                  style={{ 
                    width: '100%',
                    backgroundColor: selectedRows.length === 0 ? undefined : '#1890ff',
                    borderColor: selectedRows.length === 0 ? undefined : '#1890ff',
                    color: selectedRows.length === 0 ? undefined : 'white'
                  }}
                >
                  云打印（东）
                </Button>
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Button 
                  icon={<PrinterOutlined />} 
                  onClick={() => handlePrintClick('receipt', WEST_PRINTER_SN)}
                  disabled={selectedRows.length === 0}
                  style={{ 
                    width: '100%',
                    backgroundColor: selectedRows.length === 0 ? undefined : '#1677ff',
                    borderColor: selectedRows.length === 0 ? undefined : '#1677ff',
                    color: selectedRows.length === 0 ? undefined : 'white'
                  }}
                >
                  云打印（西）
                </Button>
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Button 
                  icon={<TagsOutlined />} 
                  onClick={() => handlePrintClick('label')}
                  disabled={selectedRows.length === 0}
                  style={{ 
                    width: '100%',
                    backgroundColor: selectedRows.length === 0 ? undefined : '#52c41a',
                    borderColor: selectedRows.length === 0 ? undefined : '#52c41a',
                    color: selectedRows.length === 0 ? undefined : 'white'
                  }}
                >
                  云标签
                </Button>
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Button 
                  icon={<PrinterOutlined />} 
                  onClick={() => handlePrint140Click()}
                  disabled={selectedRows.length === 0}
                  style={{ 
                    width: '100%',
                    backgroundColor: selectedRows.length === 0 ? undefined : '#fa8c16',
                    borderColor: selectedRows.length === 0 ? undefined : '#fa8c16',
                    color: selectedRows.length === 0 ? undefined : 'white'
                  }}
                >
                  140小单
                </Button>
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Button 
                  icon={<PlusOutlined />} 
                  onClick={() => showModal()}
                  type="primary"
                  style={{ width: '100%' }}
                >
                  新增
                </Button>
              </Col>
            </Row>
          </div>
          
          <Table
            columns={columns}
            dataSource={filteredOrders}
            rowKey="id"
            loading={loading}
            scroll={{ x: tableScrollX, y: tableHeight }}
        onChange={(_, __, sorter) => {
          if (!Array.isArray(sorter)) {
            setTableSorter({
              field: (sorter as any)?.field,
              order: (sorter as any)?.order || undefined,
            });
          }
        }}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedRowKeys,
              onChange: handleRowSelectionChange,
              type: 'checkbox',
              getCheckboxProps: (record) => ({
                name: record.order_no,
              }),
            }}
            summary={() => {
              const summary = calculatePageSummary();
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                    {/* 选择框列占位 */}
                    <Table.Summary.Cell index={0}>
                      {/* 选择框 */}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <Text strong>合计</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      {/* 收货人电话列留空 */}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      {/* 货物名称列留空 */}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>
                      {/* 总件数列留空 */}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>
                      <Text>¥{formatCurrency(summary.freight)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>
                      <Text>¥{formatCurrency(summary.delivery_install_fee)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>
                      <Text>¥{formatCurrency(summary.advance_transfer_fee)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={8}>
                      <Text>¥{formatCurrency(summary.collection_amount)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={9}>
                      <Text>¥{formatCurrency(summary.rebate)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={10}>
                      <Text>¥{formatCurrency(summary.cash_payment)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={11}>
                      <Text>¥{formatCurrency(summary.cod_total)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={12} colSpan={11}>
                      {/* 其他列留空 */}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>
      )}

        {/* 打印按钮区域 */}
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <Row gutter={[8, 8]} justify="start">
            <Col xs={24} sm={8} md={4}>
              <Button 
                icon={<PrinterOutlined />} 
                onClick={() => handlePrintClick('receipt', EAST_PRINTER_SN)}
                disabled={selectedRows.length === 0}
                style={{ 
                  width: '100%',
                  backgroundColor: selectedRows.length === 0 ? undefined : '#1890ff',
                  borderColor: selectedRows.length === 0 ? undefined : '#1890ff',
                  color: selectedRows.length === 0 ? undefined : 'white'
                }}
              >
                云打印（东）
              </Button>
            </Col>
            <Col xs={24} sm={8} md={4}>
              <Button 
                icon={<PrinterOutlined />} 
                onClick={() => handlePrintClick('receipt', WEST_PRINTER_SN)}
                disabled={selectedRows.length === 0}
                style={{ 
                  width: '100%',
                  backgroundColor: selectedRows.length === 0 ? undefined : '#1677ff',
                  borderColor: selectedRows.length === 0 ? undefined : '#1677ff',
                  color: selectedRows.length === 0 ? undefined : 'white'
                }}
              >
                云打印（西）
              </Button>
            </Col>
            <Col xs={24} sm={8} md={4}>
              <Button 
                icon={<TagsOutlined />} 
                onClick={() => handlePrintClick('label')}
                disabled={selectedRows.length === 0}
                style={{ 
                  width: '100%',
                  backgroundColor: selectedRows.length === 0 ? undefined : '#52c41a',
                  borderColor: selectedRows.length === 0 ? undefined : '#52c41a',
                  color: selectedRows.length === 0 ? undefined : 'white'
                }}
              >
                云标签
              </Button>
            </Col>
            <Col xs={24} sm={8} md={4}>
              <Button 
                icon={<PrinterOutlined />} 
                onClick={() => handlePrint140Click()}
                disabled={selectedRows.length === 0}
                style={{ 
                  width: '100%',
                  backgroundColor: selectedRows.length === 0 ? undefined : '#fa8c16',
                  borderColor: selectedRows.length === 0 ? undefined : '#fa8c16',
                  color: selectedRows.length === 0 ? undefined : 'white'
                }}
              >
                140小单
              </Button>
            </Col>
          </Row>
        </div>

        {/* 分页 */}
        {filteredOrders.length > 0 && (
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={total}
              showSizeChanger={!isMobile}
              showQuickJumper={!isMobile}
              pageSizeOptions={['10', '20', '50', '100', '300', '500', '1000']}
              showTotal={isMobile ? 
                (total, range) => `${range[0]}-${range[1]}/${total}` : 
                (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
              }
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size || 20);
              }}
              size={isMobile ? 'small' : 'default'}
              simple={isMobile}
            />
          </div>
        )}
      </Card>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingOrder ? '编辑物流单' : '新增物流单'}
        open={modalVisible}
        onCancel={() => {
          cleanupModalState();
          setModalVisible(false);
        }}
        footer={null}
        width={isMobile ? '95%' : 1400}
        destroyOnClose
      >
        {dataLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>正在加载数据...</div>
          </div>
        ) : (
          <Form
          form={form}
          layout="vertical"
          className={isMobile ? 'mobile-one-col' : undefined}
          onFinish={handleSubmit}
          initialValues={{
            settlement_method: '到付款',
            delivery_method: '站点自提',
            total_pieces: 0,
            freight: 0.00,
            delivery_install_fee: 0.00,
            advance_transfer_fee: 0.00,
            collection_amount: 0.00,
            rebate: 0.00,
            total_freight: 0.00,
            cash_payment: 0.00,
            deduction: 0.00,
            payable_collection: 0.00,
            cod_total: 0.00,
            discount_amount: 0.00,
          }}
        >
          {isMobile && (
            <style>
              {`
              .mobile-one-col .ant-col {
                flex: 0 0 100% !important;
                max-width: 100% !important;
              }
              `}
            </style>
          )}
          {/* 第一组：收货信息 */}
          <Title level={5} style={{ marginTop: '0px', marginBottom: '8px' }}>收货信息</Title>
          {/* 第一行：收货人、收货人电话、收货人地址、卸货站点 */}
          <Row gutter={16}>
            <Col span={5}>
              <Form.Item
                name="receiver_id"
                label="收货人"
                rules={[{ required: true, message: '请选择收货人' }]}
              >
                <Select
                  showSearch
                  placeholder="请选择收货人"
                  filterOption={false}
                  optionLabelProp="displayName"
                  onSearch={(value) => {
                    // 使用防抖处理，避免立即触发渲染
                    handleReceiverSearchDebounce(value);
                  }}
                  onChange={(value, option) => {
                    const selectedOption = Array.isArray(option) ? option[0] : option;
                    if (selectedOption?.isNew) {
                      // 如果是新增选项，填入姓名（不包含[+新增]标识符）
                      form.setFieldsValue({
                        receiver_id: -1, // 设置为-1表示新增
                        receiver_name: selectedOption.newName,
                        receiver_phone: '',
                        receiver_address: '',
                      });
                    } else {
                      handleReceiverChange(value);
                    }
                  }}
                  options={getReceiverOptions(deferredReceiverSearchValue, receiverIdWatch)}
                  notFoundContent="暂无数据"
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="receiver_phone"
                label="收货人电话"
              >
                <Input placeholder="收货人电话" />
              </Form.Item>

              {/* 隐藏的收货人姓名字段 */}
              <Form.Item name="receiver_name" style={{ display: 'none' }}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item
                name="receiver_address"
                label="收货人地址"
              >
                <Input placeholder="收货人地址" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="unloading_station"
                label="卸货站点"
              >
                <Select
                  showSearch
                  placeholder="请选择卸货站点"
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleUnloadingStationChange}
                >
                  {shippingStations.map(station => (
                    <Option key={station.id} value={station.name}>
                      {station.name}
                    </Option>
                  ))}
                  {shippingStations.length === 0 && (
                    <Option value="" disabled>
                      暂无站点数据
                    </Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {/* 第二行：发货人、发货人电话、装货区、收货日期、物流单号 */}
          <Row gutter={16}>
            <Col span={5}>
              <Form.Item
                name="shipper_id"
                label="发货人"
                rules={[{ required: true, message: '请选择发货人' }]}
              >
                <Select
                  showSearch
                  placeholder="请选择发货人"
                  filterOption={false}
                  optionLabelProp="displayName"
                  onSearch={(value) => {
                    // 使用防抖处理，避免立即触发渲染
                    handleShipperSearchDebounce(value);
                  }}
                  onChange={(value, option) => {
                    const selectedOption = Array.isArray(option) ? option[0] : option;
                    console.log('🔍 模态框发货人选择时的selectedOption:', selectedOption);
                    if (selectedOption?.isNew) {
                      // 如果是新增选项，填入姓名（不包含[+新增]标识符）
                      console.log('🔍 模态框新增发货人，newName:', selectedOption.newName);
                      form.setFieldsValue({
                        shipper_id: -1, // 设置为-1表示新增
                        shipper_name: selectedOption.newName,
                        shipper_phone: '',
                      });
                    } else {
                      handleShipperChange(value);
                    }
                  }}
                  options={getShipperOptions(deferredShipperSearchValue, shipperIdWatch)}
                  notFoundContent="暂无数据"
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="shipper_phone"
                label="发货人电话"
              >
                <Input placeholder="发货人电话" />
              </Form.Item>

              {/* 隐藏的发货人姓名字段 */}
              <Form.Item name="shipper_name" style={{ display: 'none' }}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="loading_area"
                label="装货区"
                rules={[{ required: true, message: '请选择装货区' }]}
              >
                <Select
                  showSearch
                  placeholder="请选择装货区"
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleLoadingAreaChange}
                >
                  {shippingStations.map(station => (
                    <Option key={station.id} value={station.name}>
                      {station.name}
                    </Option>
                  ))}
                  {shippingStations.length === 0 && (
                    <Option value="" disabled>
                      暂无站点数据
                    </Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="receive_date"
                label="收货日期"
                rules={[{ required: true, message: '请选择收货日期' }]}
              >
                <DatePicker
                  showTime={{ format: 'HH:mm:ss' }}
                  format="YYYY-MM-DD HH:mm:ss"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="order_no"
                label="物流单号"
                rules={[{ required: true, message: '请输入物流单号' }]}
              >
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          {/* 第二组：货品信息 */}
          <Title level={5} style={{ marginTop: '8px', marginBottom: '8px' }}>货品信息</Title>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="goods_name"
                label="货物名称"
                rules={[{ required: true, message: '请输入货物名称' }]}
              >
                <Input placeholder="货物名称" />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                name="total_pieces"
                label="总件数"
                rules={[{ required: true, message: '请输入总件数' }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="总件数"
                  onFocus={handleModalTotalPiecesFocus}
                  onBlur={handleModalTotalPiecesBlur}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                name="freight"
                label="运费"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="运费"
                  prefix="¥"
                  onFocus={() => handleModalMoneyFocus('freight')}
                  onBlur={() => handleModalMoneyBlurWithReset('freight')}
                  onChange={(value) => handleCalculationFieldChange(value, 'freight')}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                name="delivery_install_fee"
                label="送货安装费"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="送货安装费"
                  prefix="¥"
                  onFocus={() => handleModalMoneyFocus('delivery_install_fee')}
                  onBlur={() => handleModalMoneyBlurWithReset('delivery_install_fee')}
                  onChange={(value) => handleCalculationFieldChange(value, 'delivery_install_fee')}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                name="advance_transfer_fee"
                label="垫付中转费"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="垫付中转费"
                  prefix="¥"
                  onFocus={() => handleModalMoneyFocus('advance_transfer_fee')}
                  onBlur={() => handleModalMoneyBlurWithReset('advance_transfer_fee')}
                  onChange={(value) => handleCalculationFieldChange(value, 'advance_transfer_fee')}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                name="collection_amount"
                label="代收货款"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="代收货款"
                  prefix="¥"
                  onFocus={() => handleModalMoneyFocus('collection_amount')}
                  onBlur={() => handleModalMoneyBlurWithReset('collection_amount')}
                  onChange={(value) => handleCalculationFieldChange(value, 'collection_amount')}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                name="rebate"
                label="返点"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="返点"
                  prefix="¥"
                  onFocus={() => handleModalMoneyFocus('rebate')}
                  onBlur={() => handleModalMoneyBlurWithReset('rebate')}
                  onChange={(value) => handleCalculationFieldChange(value, 'rebate')}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 第三组：运费支付 */}
          <Title level={5} style={{ marginTop: '8px', marginBottom: '8px' }}>运费支付</Title>
          <Row gutter={16}>
            <Col span={2}>
              <Form.Item
                name="total_freight"
                label="总运费"
              >
                <InputNumber
                  disabled
                  style={{ width: '100%' }}
                  placeholder="自动计算"
                  prefix="¥"
                />
              </Form.Item>
            </Col>
            <Col span={2}>
              <Form.Item
                name="cash_payment"
                label="现付款"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="现付款"
                  prefix="¥"
                  onFocus={() => handleModalMoneyFocus('cash_payment')}
                  onBlur={() => handleModalMoneyBlurWithReset('cash_payment')}
                  onChange={(value) => handleCalculationFieldChange(value, 'cash_payment')}
                />
              </Form.Item>
            </Col>
            <Col span={2}>
              <Form.Item
                name="deduction"
                label="代扣"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="代扣"
                  prefix="¥"
                  onFocus={() => handleModalMoneyFocus('deduction')}
                  onBlur={() => handleModalMoneyBlurWithReset('deduction')}
                  onChange={(value) => handleCalculationFieldChange(value, 'deduction')}
                />
              </Form.Item>
            </Col>
            <Col span={2}>
              <Form.Item
                name="payable_collection"
                label="应付代收款"
              >
                <InputNumber
                  disabled
                  style={{ width: '100%' }}
                  placeholder="自动计算"
                  prefix="¥"
                />
              </Form.Item>
            </Col>
            <Col span={2}>
              <Form.Item
                name="cod_total"
                label="到付金额"
              >
                <InputNumber
                  disabled
                  style={{ width: '100%' }}
                  placeholder="自动计算"
                  prefix="¥"
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                name="settlement_method"
                label="结算方式"
              >
                <Select placeholder="请选择结算方式">
                  {settlementMethodOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={2}>
              <Form.Item
                name="discount_amount"
                label="优惠金额"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="优惠金额"
                  prefix="¥"
                  onFocus={() => handleModalMoneyFocus('discount_amount')}
                  onBlur={() => handleModalMoneyBlurWithReset('discount_amount')}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                name="delivery_method"
                label="送货方式"
              >
                <Select placeholder="请选择送货方式">
                  {deliveryMethodOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="business_owner_id"
                label="业务归属"
              >
                <Select
                  showSearch
                  placeholder="请选择业务归属"
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleBusinessOwnerChange}
                >
                  {employees.map(employee => (
                    <Option key={employee.id} value={employee.id}>
                      {employee.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* 第四组：收货备注 */}
          <Row gutter={16} style={{ marginTop: '16px' }}>
            <Col span={24}>
              <Form.Item
                name="receive_remark"
                label="收货备注"
              >
                <TextArea
                  rows={4}
                  placeholder="请输入收货备注"
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 表单操作按钮 */}
          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Space>
              <Checkbox 
                checked={hideCodAmount}
                onChange={(e) => {
                  console.log('🔘 弹窗复选框状态变化:', e.target.checked);
                  setHideCodAmount(e.target.checked);
                }}
                style={{ marginRight: 8 }}
              >
                隐藏到付金额
              </Checkbox>
              <Button onClick={() => {
                cleanupModalState();
                setModalVisible(false);
              }}>
                取消
              </Button>
              {!editingOrder && (
                <Button 
                  icon={<PrinterOutlined />}
                  onClick={() => {
                    createAndPrintPrinterSnRef.current = EAST_PRINTER_SN;
                    setCreateAndPrintPrinterSn(EAST_PRINTER_SN);
                    form.submit();
                  }}
                  style={{ 
                    backgroundColor: '#1890ff',
                    borderColor: '#1890ff',
                    color: 'white'
                  }}
                >
                  创建并打印（东）
                </Button>
              )}
              {!editingOrder && (
                <Button 
                  icon={<PrinterOutlined />}
                  onClick={() => {
                    createAndPrintPrinterSnRef.current = WEST_PRINTER_SN;
                    setCreateAndPrintPrinterSn(WEST_PRINTER_SN);
                    form.submit();
                  }}
                  style={{ 
                    backgroundColor: '#1677ff',
                    borderColor: '#1677ff',
                    color: 'white'
                  }}
                >
                  创建并打印（西）
                </Button>
              )}
              <Button type="primary" htmlType="submit">
                {editingOrder ? '更新' : '创建'}
              </Button>
            </Space>
          </div>
          </Form>
        )}
      </Modal>

      {/* 详情查看抽屉 */}
      <Drawer
        title="物流单详情"
        placement={isMobile ? 'bottom' : 'right'}
        width={isMobile ? '100%' : 600}
        height={isMobile ? '80%' : undefined}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {viewingOrder && (
          <div>
            <Title level={5}>收货信息</Title>
            <Row gutter={16}>
              <Col xs={24} sm={8} md={6}>
                <Text strong>收货人：</Text>
                <Text>{viewingOrder.receiver_name}</Text>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Text strong>收货人电话：</Text>
                <Text>{viewingOrder.receiver_phone}</Text>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Text strong>收货人地址：</Text>
                <Text>{viewingOrder.receiver_address}</Text>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Text strong>卸货站点：</Text>
                <Text>{viewingOrder.unloading_station}</Text>
              </Col>
            </Row>
            
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col xs={24} sm={8} md={6}>
                <Text strong>发货人：</Text>
                <Text>{viewingOrder.shipper_name}</Text>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Text strong>发货人电话：</Text>
                <Text>{viewingOrder.shipper_phone}</Text>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Text strong>装货区：</Text>
                <Text>{viewingOrder.loading_area}</Text>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Text strong>收货日期：</Text>
                <Text>{viewingOrder.receive_date ? dayjs(viewingOrder.receive_date).format('YYYY-MM-DD HH:mm:ss') : '-'}</Text>
              </Col>
            </Row>
            
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col xs={24} sm={8} md={6}>
                <Text strong>物流单号：</Text>
                <Text>{viewingOrder.order_no}</Text>
              </Col>
            </Row>

            <Title level={5} style={{ marginTop: 24 }}>货品信息</Title>
            <Row gutter={16}>
              <Col xs={24} sm={8} md={6}>
                <Text strong>货物名称：</Text>
                <Text>{viewingOrder.goods_name}</Text>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Text strong>总件数：</Text>
                <Text>{viewingOrder.total_pieces}</Text>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Text strong>运费：</Text>
                <Text>¥{formatCurrency(viewingOrder.freight)}</Text>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Text strong>送货安装费：</Text>
                <Text>¥{formatCurrency(viewingOrder.delivery_install_fee)}</Text>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Text strong>垫付中转费：</Text>
                <Text>¥{formatCurrency(viewingOrder.advance_transfer_fee)}</Text>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Text strong>代收货款：</Text>
                <Text>¥{formatCurrency(viewingOrder.collection_amount)}</Text>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Text strong>返点：</Text>
                <Text>¥{formatCurrency(viewingOrder.rebate)}</Text>
              </Col>
            </Row>

            <Title level={5} style={{ marginTop: 24 }}>运费支付</Title>
            <Row gutter={16}>
              <Col xs={24} sm={8} md={3}>
                <Text strong>总运费：</Text>
                <Text>¥{formatCurrency(viewingOrder.total_freight)}</Text>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Text strong>现付款：</Text>
                <Text>¥{formatCurrency(viewingOrder.cash_payment)}</Text>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Text strong>代扣：</Text>
                <Text>¥{formatCurrency(viewingOrder.deduction)}</Text>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Text strong>应付代收款：</Text>
                <Text>¥{formatCurrency(viewingOrder.payable_collection)}</Text>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Text strong>到付金额：</Text>
                <Text>¥{formatCurrency(viewingOrder.cod_total)}</Text>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Text strong>结算方式：</Text>
                <Text>{viewingOrder.settlement_method}</Text>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Text strong>优惠金额：</Text>
                <Text>¥{formatCurrency(viewingOrder.discount_amount)}</Text>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Text strong>送货方式：</Text>
                <Text>{viewingOrder.delivery_method}</Text>
              </Col>
            </Row>
            
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col xs={24} sm={8} md={6}>
                <Text strong>业务归属：</Text>
                <Text>{viewingOrder.business_owner_name}</Text>
              </Col>
            </Row>

            <Title level={5} style={{ marginTop: 24 }}>其他信息</Title>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={12}>
                <Text strong>收货备注：</Text>
                <Text>{viewingOrder.receive_remark || '-'}</Text>
              </Col>
            </Row>
          </div>
        )}
      </Drawer>

      {/* 批量导入模态框 */}
      <Modal
        title="批量导入物流单"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <div style={{ marginBottom: '16px' }}>
          <Upload
            beforeUpload={handleFileUpload}
            accept=".csv,.xlsx,.xls"
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>选择文件</Button>
          </Upload>
          <span style={{ marginLeft: '8px', color: '#666' }}>
            支持CSV、Excel格式，第一行为表头
          </span>
        </div>

        {importData.length > 0 && (
          <>
            <Divider>字段映射</Divider>
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ marginBottom: '12px' }}>请将源文件字段映射到目标字段</h4>
              
              <div style={{ 
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f0f8ff',
                border: '1px solid #91d5ff',
                borderRadius: '4px'
              }}>
                <Text strong style={{ color: '#1890ff' }}>字段映射说明：</Text>
                <Text style={{ marginLeft: '8px' }}>
                  系统会自动处理收货人和发货人，如果不存在会自动创建
                </Text>
              </div>
              
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {Object.keys(fieldMapping).map((field) => {
                  const targetField = fieldMapping[field];
                  
                  return (
                    <div key={field} style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px 0',
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <div style={{ 
                        flex: '1',
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        border: '1px solid #d9d9d9',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <span style={{ fontWeight: '500', color: '#1890ff' }}>{field}</span>
                      </div>
                      
                      <div style={{ 
                        fontSize: '18px',
                        color: '#1890ff',
                        fontWeight: 'bold',
                        padding: '0 8px'
                      }}>
                        →
                      </div>
                      
                      <div style={{ flex: '1' }}>
                        <Select
                          value={fieldMapping[field]}
                          onChange={(value) => setFieldMapping({ ...fieldMapping, [field]: value })}
                          style={{ width: '100%' }}
                          placeholder="请选择对应字段"
                        >
                          <Option value="物流单号">物流单号</Option>
                          <Option value="收货日期">收货日期</Option>
                          <Option value="发货人姓名">发货人姓名</Option>
                          <Option value="发货人电话">发货人电话</Option>
                          <Option value="装货区">装货区</Option>
                          <Option value="卸货站点">卸货站点</Option>
                          <Option value="收货人姓名">收货人姓名</Option>
                          <Option value="收货人电话">收货人电话</Option>
                          <Option value="收货人地址">收货人地址</Option>
                          <Option value="货物名称">货物名称</Option>
                          <Option value="总件数">总件数</Option>
                          <Option value="运费">运费</Option>
                          <Option value="送货安装费">送货安装费</Option>
                          <Option value="垫付费">垫付费</Option>
                          <Option value="代收款">代收款</Option>
                          <Option value="结算方式">结算方式</Option>
                          <Option value="送货方式">送货方式</Option>
                          <Option value="业务归属">业务归属</Option>
                          <Option value="收货备注">收货备注</Option>
                          <Option value="状态">状态</Option>
                          <Option value="skip">不导入</Option>
                        </Select>
                      </div>
                      
                      <div style={{ 
                        width: '60px',
                        textAlign: 'center'
                      }}>
                        {targetField && (
                          <Tag color={
                            targetField === 'skip' ? 'default' : 'green'
                          }>
                            {targetField === 'skip' ? '不导入' : '导入'}
                          </Tag>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Divider>数据预览（前5条）</Divider>
            <Table
              dataSource={importData.slice(0, 5)}
              columns={Object.keys(fieldMapping).map(field => ({
                title: field,
                dataIndex: field,
                key: field,
              }))}
              pagination={false}
              size="small"
            />

            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setImportModalVisible(false)}>
                  取消
                </Button>
                <Button 
                  type="primary" 
                  onClick={handleImport}
                  loading={importing}
                >
                  开始导入
                </Button>
              </Space>
            </div>
          </>
        )}

        {/* 导入结果 */}
        {importResult && (
          <>
            <Divider>导入结果</Divider>
            
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col xs={24} sm={8} md={4}>
                <Statistic 
                  title="成功导入" 
                  value={importResult.successCount || 0} 
                  valueStyle={{ color: '#3f8600' }}
                  suffix="条记录"
                />
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Statistic 
                  title="跳过记录" 
                  value={importResult.skipCount || 0} 
                  valueStyle={{ color: '#faad14' }}
                  suffix="条记录"
                />
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Statistic 
                  title="失败记录" 
                  value={importResult.errorCount || 0} 
                  valueStyle={{ color: '#cf1322' }}
                  suffix="条记录"
                />
              </Col>
            </Row>
            
            {importResult.errors && importResult.errors.length > 0 && (
              <div style={{ 
                marginTop: '16px',
                padding: '16px',
                backgroundColor: '#fff2f0',
                border: '1px solid #ffccc7',
                borderRadius: '4px'
              }}>
                <h4 style={{ color: '#cf1322', marginBottom: '12px' }}>详细说明：</h4>
                
                <div style={{ marginTop: '12px' }}>
                  <Text strong>具体错误：</Text>
                  {importResult.errors.map((error: any, index: number) => (
                    <div key={index} style={{ 
                      color: '#cf1322', 
                      marginBottom: '4px',
                      padding: '4px 8px',
                      backgroundColor: '#fff',
                      borderRadius: '2px'
                    }}>
                      <Text strong>第{error.row}行：</Text> {error.message}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <Space size="large">
                <Button 
                  type="primary" 
                  onClick={() => {
                    setImportModalVisible(false);
                    setImportData([]);
                    setFieldMapping({});
                    setImportResult(null);
                  }}
                >
                  完成并关闭
                </Button>
                <Button 
                  onClick={() => {
                    setImportData([]);
                    setFieldMapping({});
                    setImportResult(null);
                  }}
                >
                  重新导入
                </Button>
              </Space>
            </div>
          </>
        )}
      </Modal>

      {/* 打印相关模态框 */}
      {/* 设备选择模态框 */}
      <Modal
        title="选择打印设备"
        open={deviceSelectionVisible}
        onCancel={() => setDeviceSelectionVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text>请选择要使用的{printType === 'receipt' ? '云打印机' : '云标签机'}：</Text>
        </div>
        
        {availableDevices.length > 0 ? (
          <div>
            {availableDevices.map((device) => (
              <Card
                key={device.id}
                size="small"
                style={{ 
                  marginBottom: '8px', 
                  cursor: 'pointer',
                  border: selectedDevice?.id === device.id ? '2px solid #1890ff' : '1px solid #d9d9d9'
                }}
                onClick={() => setSelectedDevice(device)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>{device.device_name || device.device_sn}</Text>
                    <br />
                    <Text type="secondary">设备编号: {device.device_sn}</Text>
                  </div>
                  <div>
                    {selectedDevice?.id === device.id && (
                      <Text type="success">✓ 已选择</Text>
                    )}
                  </div>
                </div>
              </Card>
            ))}
            
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <Space>
                <Button 
                  type="primary" 
                  onClick={() => {
                    if (selectedDevice) {
                      setDeviceSelectionVisible(false);
                      if (selectedRows.length > 0) {
                        handleBatchPrintWithDevice(selectedDevice, selectedRows, printType);
                      } else {
                        messageApi.warning('请先选择一个或多个物流单');
                      }
                    } else {
                      messageApi.warning('请先选择一个设备');
                    }
                  }}
                  disabled={!selectedDevice}
                >
                  确认打印
                </Button>
                <Button onClick={() => setDeviceSelectionVisible(false)}>
                  取消
                </Button>
              </Space>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Text type="secondary">没有找到可用的{printType === 'receipt' ? '云打印机' : '云标签机'}</Text>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default LogisticsOrderManagement;
// 添加选中行的样式
const styles = `
  .selected-row {
    background-color: #e6f7ff !important;
    border-left: 3px solid #1890ff !important;
  }
  
  .selected-row:hover {
    background-color: #bae7ff !important;
  }
  
  .selected-row td {
    background-color: transparent !important;
  }
  
  .selected-row .ant-table-cell {
    background-color: transparent !important;
  }
  
  /* 移动端优化 */
  @media (max-width: 768px) {
    .selected-row {
      background-color: #f0f9ff !important;
      border-left: 4px solid #1890ff !important;
    }
    
    .selected-row:hover {
      background-color: #e0f2fe !important;
    }
  }
  
  /* 选中图标动画 */
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  .fadeIn {
    animation: fadeIn 0.3s ease-in-out;
  }

  /* 140小单打印样式 */
  @media print {
    #print140-content > div {
      page-break-after: always !important;
      page-break-inside: avoid !important;
    }
    
    #print140-content > div:last-child {
      page-break-after: auto !important;
    }
    
    .ant-modal-content {
      box-shadow: none !important;
    }
    
    .ant-modal-header,
    .ant-modal-footer {
      display: none !important;
    }
    
    .ant-modal-body {
      padding: 0 !important;
    }
  }
`;

// 动态添加样式到页面
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = styles;
  document.head.appendChild(styleElement);
}


